import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import edrmsApi, { OUT_STATUS, IN_STATUS, RECEIVED_VIA, ACTIONS, fmtDate } from '../utils/edrmsApi';
import useLiveRefresh from '../utils/useLiveRefresh';

const ACCENT = '#B45309';
const btn = (bg = ACCENT, color = '#fff') => ({ padding: '8px 16px', borderRadius: 8, border: 'none', background: bg, color, fontSize: 13, fontWeight: 700, cursor: 'pointer' });
const inp = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
const lbl = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: '#9CA3AF', marginBottom: 5, display: 'block' };
const card = { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 18, marginBottom: 16 };

function Pill({ meta }) {
  if (!meta) return null;
  return <span style={{ fontSize: 11, fontWeight: 700, color: meta.color, background: meta.bg, padding: '3px 9px', borderRadius: 999 }}>{meta.label}</span>;
}

function Modal({ title, onClose, children, width = 460 }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width, maxWidth: '100%', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #F3F0EA' }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0, flex: 1 }}>{title}</h3>
          <button onClick={onClose} style={{ ...btn('transparent', '#9CA3AF'), fontSize: 20, padding: 0, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

export default function EdrmsPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [view, setView] = useState('dashboard'); // dashboard|outgoing|incoming|admin:settings|admin:roles|admin:groups|admin:departments|admin:stamps
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => { edrmsApi.get('/me').then(setMe).catch(() => setMe({ has_access: false })); }, []);

  if (!me) return <div style={{ padding: 40, color: '#6B7280' }}>Loading…</div>;
  if (!me.has_access) {
    return (
      <div style={{ padding: 40, textAlign: 'center', maxWidth: 460, margin: '40px auto' }}>
        <div style={{ fontSize: 40 }}>📁</div>
        <h2 style={{ fontSize: 18, fontWeight: 800 }}>EDRMS — no access</h2>
        <p style={{ fontSize: 13.5, color: '#6B7280' }}>You haven't been granted access to EDRMS. A super admin can assign you as an Admin or Member.</p>
        <button onClick={() => navigate('/')} style={{ ...btn('#F3F4F6', '#111827'), marginTop: 12 }}>← Back to apps</button>
      </div>
    );
  }

  const years = [];
  for (let y = new Date().getFullYear() + 1; y >= 2023; y--) years.push(y);

  const NavItem = ({ id, icon, label, indent }) => (
    <button onClick={() => setView(id)} style={{
      display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
      padding: indent ? '8px 14px 8px 34px' : '10px 14px', border: 'none', borderRadius: 9, cursor: 'pointer',
      background: view === id ? ACCENT : 'transparent', color: view === id ? '#fff' : '#374151',
      fontWeight: view === id ? 700 : 500, fontSize: 13.5, marginBottom: 2,
    }}>
      {icon && <span style={{ fontSize: 15 }}>{icon}</span>}<span>{label}</span>
    </button>
  );
  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 10.5, fontWeight: 800, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '.6px', padding: '14px 14px 6px' }}>{children}</div>
  );

  return (
    <div style={{ height: '100vh', background: '#F8F7F4', display: 'flex', overflow: 'hidden' }}>
      {/* Sidebar */}
      <aside style={{ width: 244, background: '#fff', borderRight: '1px solid #ECE9E3', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '18px 16px 10px', borderBottom: '1px solid #F3F0EA' }}>
          <button onClick={() => navigate('/')} style={{ ...btn('transparent', '#9CA3AF'), padding: '2px 0', fontSize: 12, fontWeight: 600 }}>← All apps</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 8 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: ACCENT, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📁</div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 850, lineHeight: 1 }}>EDRMS</div>
              <div style={{ fontSize: 10.5, color: '#9CA3AF', marginTop: 2 }}>Correspondence Control</div>
            </div>
          </div>
        </div>
        <div style={{ padding: '10px 10px', flex: 1, overflowY: 'auto' }}>
          <NavItem id="dashboard" icon="📊" label="Dashboard" />
          <NavItem id="outgoing" icon="📤" label="Outgoing (الصادر)" />
          <NavItem id="incoming" icon="📥" label="Incoming (الوارد)" />
          <NavItem id="records" icon="🗂️" label="Document Register" />
          {me.is_admin && <>
            <SectionLabel>Administration</SectionLabel>
            <NavItem id="admin:settings" label="Numbering & Settings" indent />
            <NavItem id="admin:roles" label="Members & Roles" indent />
            <NavItem id="admin:signatories" label="Signatories" indent />
            <NavItem id="admin:groups" label="Groups" indent />
            <NavItem id="admin:departments" label="Departments" indent />
            <NavItem id="admin:stamps" label="Stamps" indent />
            <NavItem id="admin:rectypes" label="Document Types" indent />
          </>}
        </div>
        <div style={{ padding: '12px 14px', borderTop: '1px solid #F3F0EA' }}>
          <label style={{ fontSize: 10.5, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>Year</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inp, marginTop: 4 }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <a href={`/api/edrms/register.xlsx?year=${year}&token=${localStorage.getItem('flowup_token')}`} style={{ ...btn('#16A34A'), textDecoration: 'none', display: 'block', textAlign: 'center', marginTop: 8 }}>⬇ Excel Register</a>
        </div>
      </aside>

      {/* Content */}
      <main id="edrms-scroll" style={{ flex: 1, overflowY: 'auto', padding: '24px 30px 80px' }}>
        <div style={{ maxWidth: 1500, margin: '0 auto' }}>
          {view === 'dashboard' && <Dashboard year={year} />}
          {view === 'outgoing' && <OutgoingSection year={year} me={me} />}
          {view === 'incoming' && <IncomingSection year={year} me={me} />}
          {view === 'records' && <RecordsSection me={me} />}
          {view === 'admin:rectypes' && me.is_admin && <AdminRecordTypes />}
          {view === 'admin:settings' && me.is_admin && <AdminSettings />}
          {view === 'admin:roles' && me.is_admin && <AdminRoles />}
          {view === 'admin:signatories' && me.is_admin && <AdminSignatories />}
          {view === 'admin:groups' && me.is_admin && <AdminGroups />}
          {view === 'admin:departments' && me.is_admin && <AdminDepartments />}
          {view === 'admin:stamps' && me.is_admin && <AdminStamps />}
        </div>
      </main>
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ year }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => edrmsApi.get(`/dashboard?year=${year}`).then(setD).catch(() => {}), [year]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load);
  if (!d) return <div style={{ color: '#6B7280' }}>Loading…</div>;
  const stat = (label, val, color, sub) => (
    <div style={{ background: '#fff', border: '1px solid #ECE9E3', borderRadius: 14, padding: '18px 20px', flex: 1, minWidth: 160 }}>
      <div style={{ fontSize: 30, fontWeight: 850, color: color || '#111827', lineHeight: 1 }}>{val ?? 0}</div>
      <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 6 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{sub}</div>}
    </div>
  );
  return (
    <div>
      <PageHead title="Dashboard" desc={`Overview of correspondence for ${year}.`} />
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {stat('Outgoing this month', d.outgoing_this_month, ACCENT)}
        {stat(`Outgoing in ${year}`, d.outgoing_total, '#0D9488')}
        {stat('Pending signature', d.pending_signature, '#7C3AED')}
        {stat(`Incoming in ${year}`, d.incoming_total, '#2563EB')}
        {stat('Incoming open', d.incoming_open, '#D97706')}
        {stat('Documents registered', d.records_total, '#7C3AED')}
        {stat('Cancelled', d.cancelled, '#9CA3AF')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        <div style={card}>
          <h3 style={{ fontSize: 14, fontWeight: 800, marginTop: 0, marginBottom: 4 }}>Monthly correspondence — {year}</h3>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>Outgoing vs incoming per month</div>
          <MonthlyBars data={d.monthly || []} />
        </div>
        <div style={card}>
          <h3 style={{ fontSize: 14, fontWeight: 800, marginTop: 0, marginBottom: 4 }}>Document Register</h3>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>{d.records_total || 0} record(s){d.records_locked ? ` · ${d.records_locked} 🔒 locked` : ''}</div>
          <BarList data={(d.records_by_type || []).map(x => ({ label: x.type, value: x.c }))} color="#7C3AED" empty="No documents registered yet." />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <div style={card}>
          <h3 style={{ fontSize: 14, fontWeight: 800, marginTop: 0, marginBottom: 10 }}>Incoming by department</h3>
          <BarList data={(d.in_by_dept || []).map(x => ({ label: x.dept, value: x.c }))} color="#2563EB" empty="No incoming yet." />
        </div>
        <div style={card}>
          <h3 style={{ fontSize: 14, fontWeight: 800, marginTop: 0, marginBottom: 10 }}>Outgoing by organization</h3>
          <BarList data={(d.by_org || []).map(x => ({ label: x.org, value: x.c }))} color={ACCENT} empty="No outgoing yet." />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <div style={card}>
          <h3 style={{ fontSize: 14, fontWeight: 800, marginTop: 0, marginBottom: 4 }}>Outgoing status</h3>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>Breakdown for {year}</div>
          <Donut data={(d.out_status || []).map(s => ({ label: (OUT_STATUS[s.status]?.label || s.status), value: s.c }))} />
        </div>
        <div style={card}>
          <h3 style={{ fontSize: 14, fontWeight: 800, marginTop: 0, marginBottom: 4 }}>Incoming status</h3>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>Breakdown for {year}</div>
          <Donut data={(d.in_status || []).map(s => ({ label: (IN_STATUS[s.status]?.label || s.status), value: s.c }))} />
        </div>
      </div>
    </div>
  );
}

// ── small inline-SVG charts (no external chart library) ──
function MonthlyBars({ data }) {
  const max = Math.max(1, ...data.map(d => Math.max(d.outgoing, d.incoming)));
  const W = 520, H = 180, padL = 24, padB = 22, padT = 8;
  const bw = (W - padL) / data.length;
  const y = v => padT + (H - padT - padB) * (1 - v / max);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      {[0, 0.5, 1].map((f, i) => <line key={i} x1={padL} x2={W} y1={y(max * f)} y2={y(max * f)} stroke="#F0EDE7" />)}
      {data.map((d, i) => {
        const x = padL + i * bw;
        const halfW = bw * 0.3;
        return (
          <g key={i}>
            <rect x={x + bw * 0.15} y={y(d.outgoing)} width={halfW} height={H - padB - y(d.outgoing)} fill="#0D9488" rx={2} />
            <rect x={x + bw * 0.52} y={y(d.incoming)} width={halfW} height={H - padB - y(d.incoming)} fill="#2563EB" rx={2} />
            <text x={x + bw / 2} y={H - 7} fontSize="9" fill="#9CA3AF" textAnchor="middle">{d.month}</text>
          </g>
        );
      })}
      <g fontSize="10">
        <rect x={padL} y={padT} width={9} height={9} fill="#0D9488" /><text x={padL + 13} y={padT + 8} fill="#6B7280">Outgoing</text>
        <rect x={padL + 70} y={padT} width={9} height={9} fill="#2563EB" /><text x={padL + 83} y={padT + 8} fill="#6B7280">Incoming</text>
      </g>
    </svg>
  );
}

function Donut({ data }) {
  const COLORS = ['#0D9488', '#2563EB', '#7C3AED', '#D97706', '#DC2626', '#9CA3AF', '#059669'];
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return <div style={{ fontSize: 13, color: '#9CA3AF' }}>No data.</div>;
  let acc = 0; const R = 60, r = 38, cx = 70, cy = 70;
  const arc = (frac0, frac1) => {
    const a0 = frac0 * 2 * Math.PI - Math.PI / 2, a1 = frac1 * 2 * Math.PI - Math.PI / 2;
    const x0 = cx + R * Math.cos(a0), y0 = cy + R * Math.sin(a0), x1 = cx + R * Math.cos(a1), y1 = cy + R * Math.sin(a1);
    const xi1 = cx + r * Math.cos(a1), yi1 = cy + r * Math.sin(a1), xi0 = cx + r * Math.cos(a0), yi0 = cy + r * Math.sin(a0);
    const large = frac1 - frac0 > 0.5 ? 1 : 0;
    return `M${x0} ${y0} A${R} ${R} 0 ${large} 1 ${x1} ${y1} L${xi1} ${yi1} A${r} ${r} 0 ${large} 0 ${xi0} ${yi0} Z`;
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg viewBox="0 0 140 140" style={{ width: 140, height: 140 }}>
        {data.map((d, i) => { const f0 = acc / total; acc += d.value; const f1 = acc / total; return <path key={i} d={arc(f0, f1)} fill={COLORS[i % COLORS.length]} />; })}
        <text x="70" y="74" textAnchor="middle" fontSize="20" fontWeight="800" fill="#111827">{total}</text>
      </svg>
      <div style={{ fontSize: 12 }}>
        {data.map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length], display: 'inline-block' }} />
            <span style={{ color: '#374151' }}>{d.label}</span><b style={{ marginLeft: 'auto' }}>{d.value}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarList({ data, color, empty }) {
  if (!data.length) return <div style={{ fontSize: 13, color: '#9CA3AF' }}>{empty}</div>;
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div>
      {data.map((d, i) => (
        <div key={i} style={{ padding: '6px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 3 }}>
            <span style={{ color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>{d.label}</span><b>{d.value}</b>
          </div>
          <div style={{ height: 7, background: '#F3F0EA', borderRadius: 999 }}>
            <div style={{ width: `${(d.value / max) * 100}%`, height: '100%', background: color, borderRadius: 999 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Outgoing ─────────────────────────────────────────────────────────────────
function OutgoingSection({ year, me }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);   // letter id being viewed
  const [openPw, setOpenPw] = useState(null); // password for a locked letter being opened
  const [unlockFor, setUnlockFor] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback((silent) => edrmsApi.get(`/outgoing?year=${year}`).then(setRows).catch(() => setRows([])), [year]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(true));
  useEffect(() => { if (open || creating) document.getElementById('edrms-scroll')?.scrollTo(0, 0); }, [open, creating]);

  const openRow = (r) => { if (r.is_locked) { setUnlockFor(r); } else { setOpenPw(null); setOpen(r.id); } };

  if (creating) return <OutgoingForm me={me} onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); load(); setOpen(id); }} />;
  if (open) return <OutgoingDetail id={open} me={me} lockPw={openPw} onBack={() => { setOpen(null); setOpenPw(null); load(); document.getElementById('edrms-scroll')?.scrollTo(0, 0); }} />;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0, flex: 1 }}>Outgoing Letters (الصادر)</h3>
        <button onClick={() => setCreating(true)} style={btn()}>+ New Letter</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
          <thead><tr style={{ textAlign: 'left', fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase' }}>
            {['Reference', 'Date', 'Recipient', 'Subject', 'Lang', 'Status'].map(h => <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid #E5E7EB' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} style={{ padding: 20, color: '#9CA3AF' }}>No outgoing letters yet.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} onClick={() => openRow(r)} style={{ cursor: 'pointer', borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '10px', fontWeight: 700 }}>{r.is_locked ? '🔒 ' : ''}{r.reference}{r.status === 'cancelled' ? ' — Cancelled' : ''}</td>
                <td style={{ padding: '10px', fontSize: 12.5 }}>{r.is_locked ? '—' : fmtDate(r.letter_date)}</td>
                <td style={{ padding: '10px', fontSize: 12.5 }}>{r.is_locked ? <span style={{ color: '#9CA3AF' }}>— locked —</span> : <>{r.recipient_name}<div style={{ color: '#9CA3AF', fontSize: 11 }}>{r.recipient_org}</div></>}</td>
                <td style={{ padding: '10px', fontSize: 12.5 }}>{r.is_locked ? '' : r.subject}</td>
                <td style={{ padding: '10px', fontSize: 12 }}>{r.language === 'ar' ? 'AR' : 'EN'}</td>
                <td style={{ padding: '10px' }}><Pill meta={OUT_STATUS[r.status]} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {unlockFor && <UnlockModal kind="outgoing" item={unlockFor} onClose={() => setUnlockFor(null)}
        onUnlocked={(pw) => { setUnlockFor(null); setOpenPw(pw); setOpen(unlockFor.id); }} />}
    </div>
  );
}
// Renders the recipient block the way it will look in the letter (Honorific / Name,
// Title, Organization stacked, right-aligned, in the letter's real Arabic font) and
// draws a TARGET line at the widest line's end. The user adds/removes tatweel (ـ) per
// line with −/+ buttons (the ـ goes into the actual field value, so it carries to the
// letter) and watches the lines line up — a WYSIWYG simulator, like doing it in Word.
// Measurement uses the live rendered DOM, so the hint and guide are exact for what's
// shown (and match Word closely when the browser has the same Traditional Arabic font).
// Measure + compute tatweel entirely in the browser, in the SAME font Word uses for
// the recipient lines: Calibri (Body), bold, 14pt. On Windows (where these letters are
// made) the browser has real Calibri, so this is exact. Carlito is a metric-compatible
// fallback. We measure with canvas measureText (which shapes Arabic), and converge by
// inserting tatweels and re-measuring — so counts never overshoot.
const TAT = '\u0640';
const REC_FONT = "Calibri, Carlito, 'Segoe UI', Tahoma, sans-serif";
let _recCtx = null;
function _recMeasure(text, weight) {
  if (!_recCtx) { try { _recCtx = document.createElement('canvas').getContext('2d'); } catch (e) { return 0; } }
  // measure at a large size for sub-pixel precision; ratios are size-independent
  _recCtx.font = (weight || 'bold') + " 64px " + REC_FONT;
  return _recCtx.measureText(text).width;
}
function _recInsert(text, n) {
  if (n <= 0 || !text) return text;
  const t = text.replace(/\s+$/, '');
  const trail = text.slice(t.length);
  if (!t) return text;
  return t.slice(0, -1) + TAT.repeat(n) + t.slice(-1) + trail;
}
// Generic tatweel solver for a list of { key, label, prefix, base } lines. `weight`
// is 'bold' (recipient) or 'normal' (CC). Returns the same lines with width/tatweels/
// stretched filled in, all measured in real Calibri at that weight.
function solveTatweel(rawLines, weight) {
  const lines = rawLines.map(l => ({ ...l, base: (l.base || '').replace(/\u0640+/g, '').trim() })).filter(l => l.base);
  if (lines.length < 2) return { ready: false, lines };
  lines.forEach(l => { l.width = _recMeasure(l.prefix + l.base, weight); });
  const target = Math.max(...lines.map(l => l.width));
  lines.forEach(l => {
    if (l.width >= target) { l.tatweels = 0; l.stretched = l.base; l.finalWidth = l.width; return; }
    let prev = { n: 0, w: l.width, s: l.base };
    let res = null;
    for (let n = 1; n <= 600; n++) {
      const s = _recInsert(l.base, n);
      const w = _recMeasure(l.prefix + s, weight);
      if (w >= target) {
        res = (Math.abs(w - target) <= Math.abs(prev.w - target)) ? { n, s, w } : { n: prev.n, s: prev.s, w: prev.w };
        break;
      }
      if (w <= prev.w && n > 2) { res = { n: prev.n, s: prev.s, w: prev.w }; break; }
      prev = { n, w, s };
    }
    if (!res) res = { n: prev.n, s: prev.s, w: prev.w };
    l.tatweels = res.n; l.stretched = res.s; l.finalWidth = res.w;
  });
  return { ready: true, lines, target };
}
function computeRecipientTatweel(honorific, name, title, org) {
  return solveTatweel([
    { key: 'name', label: 'Name', prefix: (honorific || 'الفاضل') + ' / ', base: name || '' },
    { key: 'title', label: 'Title', prefix: '', base: title || '' },
    { key: 'org', label: 'Org', prefix: '', base: org || '' },
  ], 'bold');
}

function RecipientSimulator({ honorific, name, title, org, set }) {
  const spanRefs = useRef({});
  const [widths, setWidths] = useState({});
  const present = [name, title, org].filter(x => (x || '').trim()).length;

  // exact, client-side, in real Calibri — recomputed on every change
  const comp = computeRecipientTatweel(honorific, name, title, org);
  const byKey = {};
  comp.lines.forEach(l => { byKey[l.key] = l; });

  const dispLines = [
    { key: 'recipient_name', ck: 'name', label: 'Name', prefix: (honorific || 'الفاضل') + ' / ', text: name || '' },
    { key: 'recipient_title', ck: 'title', label: 'Title', prefix: '', text: title || '' },
    { key: 'recipient_org', ck: 'org', label: 'Org', prefix: '', text: org || '' },
  ].filter(l => l.text);

  useLayoutEffect(() => {
    const w = {};
    dispLines.forEach(l => { const el = spanRefs.current[l.key]; if (el) w[l.key] = el.getBoundingClientRect().width; });
    setWidths(w);
  }, [honorific, name, title, org]);

  if (present < 2) {
    return (
      <div style={{ marginBottom: 14, border: '1px dashed #D1D5DB', borderRadius: 10, padding: '12px 14px', background: '#FAFAFA', fontSize: 12.5, color: '#6B7280' }}>
        <b style={{ color: '#374151' }}>Recipient alignment helper</b> — fill at least two of Name / Title / Organization. This preview shows the lines stacked in the letter's font (Calibri, bold, 14pt) and tells you exactly how many tatweel (ـ) to add so they line up evenly, like in Word.
      </div>
    );
  }

  const vals = dispLines.map(l => widths[l.key] || 0);
  const maxW = Math.max(0, ...vals);
  const PAD = 14;

  return (
    <div style={{ marginBottom: 14, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ background: '#F9FAFB', padding: '7px 12px', fontSize: 12, fontWeight: 700, color: '#374151', borderBottom: '1px solid #E5E7EB' }}>
        Recipient preview (Calibri · bold · 14pt) — add tatweel until each line reaches the dashed line
      </div>
      {/* live preview in the real letter font */}
      <div style={{ position: 'relative', direction: 'rtl', textAlign: 'right', fontFamily: REC_FONT, fontWeight: 700, fontSize: 24, lineHeight: 1.8, color: '#1F4E79', padding: `12px ${PAD}px`, background: '#fff' }}>
        {maxW > 0 && <div style={{ position: 'absolute', top: 6, bottom: 6, right: PAD + maxW, borderRight: '2px dashed #EF4444', opacity: 0.75 }} />}
        {dispLines.map(l => (
          <div key={l.key} style={{ whiteSpace: 'nowrap', position: 'relative' }}>
            <span ref={el => spanRefs.current[l.key] = el}>
              {l.prefix && <span style={{ color: '#9CA3AF' }}>{l.prefix}</span>}
              <span>{l.text}</span>
            </span>
            {l.key === 'recipient_name' && <span style={{ color: '#C7CDD6', marginInlineStart: 24 }}>المحترم</span>}
          </div>
        ))}
      </div>
      {/* exact counts */}
      <div style={{ padding: '10px 12px', background: '#FAFAFA', borderTop: '1px solid #F0F0F0', fontSize: 12.5 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8, fontSize: 12 }}>
          <tbody>
            {dispLines.map(l => {
              const c = byKey[l.ck];
              const n = c ? c.tatweels : 0;
              const isTarget = c && c.tatweels === 0 && Math.abs((c.width || 0) - (comp.target || 0)) < 1;
              return (
                <tr key={l.key} style={{ borderBottom: '1px solid #F0F0F0' }}>
                  <td style={{ padding: '3px 6px', fontWeight: 700, width: 60 }}>{l.label}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                    {isTarget ? <span style={{ color: '#16A34A', fontWeight: 700 }}>longest (target) ✓</span>
                      : n === 0 ? <span style={{ color: '#16A34A' }}>aligned ✓</span>
                        : <span style={{ color: '#B45309', fontWeight: 700 }}>add {n} × ـ</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: '#16A34A', marginTop: 4 }}>
          ✓ Measured in real Calibri (bold, 14pt) in your browser — matches Word. Add the shown tatweel (ـ) into each field until every line reaches the dashed line.
        </div>
      </div>
    </div>
  );
}

// CC tatweel simulator — same idea as the recipient one, but the CC block prints in
// Calibri (Body) **size 12, NOT bold**, so we measure with weight 'normal'. Lines:
//   "<cc_honorific>/ <cc_name>  المحترم", "<cc_title>", "<cc_org>".
function CcSimulator({ honorific, name, title, org, set }) {
  const spanRefs = useRef({});
  const [widths, setWidths] = useState({});
  const present = [name, title, org].filter(x => (x || '').trim()).length;

  const comp = solveTatweel([
    { key: 'cc_name', label: 'Name', prefix: (honorific || 'الفاضل') + '/ ', base: name || '' },
    { key: 'cc_title', label: 'Title', prefix: '', base: title || '' },
    { key: 'cc_org', label: 'Org', prefix: '', base: org || '' },
  ], 'normal');
  const byKey = {}; comp.lines.forEach(l => { byKey[l.key] = l; });

  const dispLines = [
    { key: 'cc_name', label: 'Name', prefix: (honorific || 'الفاضل') + '/ ', text: name || '' },
    { key: 'cc_title', label: 'Title', prefix: '', text: title || '' },
    { key: 'cc_org', label: 'Org', prefix: '', text: org || '' },
  ].filter(l => l.text);

  useLayoutEffect(() => {
    const w = {};
    dispLines.forEach(l => { const el = spanRefs.current[l.key]; if (el) w[l.key] = el.getBoundingClientRect().width; });
    setWidths(w);
  }, [honorific, name, title, org]);

  if (present < 2) {
    return (
      <div style={{ marginTop: 10, border: '1px dashed #D1D5DB', borderRadius: 10, padding: '10px 12px', background: '#fff', fontSize: 12, color: '#6B7280' }}>
        <b style={{ color: '#374151' }}>CC alignment helper</b> — fill at least two of CC Name / Title / Organization to see how many tatweel (ـ) to add so the CC lines line up (Calibri 12, not bold).
      </div>
    );
  }
  const maxW = Math.max(0, ...dispLines.map(l => widths[l.key] || 0));
  const PAD = 14;
  return (
    <div style={{ marginTop: 10, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ background: '#F9FAFB', padding: '6px 12px', fontSize: 11.5, fontWeight: 700, color: '#374151', borderBottom: '1px solid #E5E7EB' }}>
        CC preview (Calibri · 12 · regular) — add tatweel until each line reaches the dashed line
      </div>
      <div style={{ position: 'relative', direction: 'rtl', textAlign: 'right', fontFamily: REC_FONT, fontWeight: 400, fontSize: 20, lineHeight: 1.8, color: '#2E74B5', padding: `12px ${PAD}px`, background: '#fff' }}>
        {maxW > 0 && <div style={{ position: 'absolute', top: 6, bottom: 6, right: PAD + maxW, borderRight: '2px dashed #EF4444', opacity: 0.75 }} />}
        {dispLines.map(l => (
          <div key={l.key} style={{ whiteSpace: 'nowrap', position: 'relative' }}>
            <span ref={el => spanRefs.current[l.key] = el}>
              {l.prefix && <span style={{ color: '#9CA3AF' }}>{l.prefix}</span>}
              <span>{l.text}</span>
            </span>
            {l.key === 'cc_name' && <span style={{ color: '#C7CDD6', marginInlineStart: 20 }}>المحترم</span>}
          </div>
        ))}
      </div>
      <div style={{ padding: '8px 12px', background: '#FAFAFA', borderTop: '1px solid #F0F0F0', fontSize: 12 }}>
        {dispLines.map(l => {
          const c = byKey[l.key]; const n = c ? c.tatweels : 0;
          const isTarget = c && c.tatweels === 0 && Math.abs((c.width || 0) - (comp.target || 0)) < 1;
          return (
            <span key={l.key} style={{ display: 'inline-block', marginRight: 12 }}>
              <b>{l.label}:</b> {isTarget ? <span style={{ color: '#16A34A', fontWeight: 700 }}>longest ✓</span> : n === 0 ? <span style={{ color: '#16A34A' }}>aligned</span> : <span style={{ color: '#B45309', fontWeight: 700 }}>add {n} × ـ</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function OutgoingForm({ me, onClose, onSaved, existing }) {
  // Normalize an existing letter so the date input shows the saved date (Postgres
  // returns a full timestamp; <input type="date"> needs YYYY-MM-DD). Without this the
  // date appears blank on edit and has to be re-picked every save.
  const initForm = existing
    ? { ...existing, letter_date: (existing.letter_date ? String(existing.letter_date).slice(0, 10) : new Date().toISOString().slice(0, 10)) }
    : { category: 'company', language: 'en', confidential: false, include_stamp: false, letter_date: new Date().toISOString().slice(0, 10) };
  const [f, setF] = useState(initForm);
  const [ref, setRef] = useState(null);
  const [signatories, setSignatories] = useState([]);
  const [stamps, setStamps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => { edrmsApi.get('/signatories').then(setSignatories).catch(() => {}); edrmsApi.get('/stamps').then(setStamps).catch(() => {}); }, []);
  useEffect(() => {
    if (existing) return;
    edrmsApi.get(`/next-number?direction=outgoing&category=${f.category}&year=${new Date(f.letter_date).getFullYear()}`).then(setRef).catch(() => {});
  }, [f.category, f.letter_date, existing]);

  const [missing, setMissing] = useState({});
  const save = async () => {
    // required fields (#3): honorific, recipient name, category, language, letter date,
    // recipient organization, subject, signatory. (Recipient title is optional.)
    const required = {
      recipient_honorific: f.recipient_honorific, recipient_name: f.recipient_name,
      category: f.category, language: f.language, letter_date: f.letter_date,
      recipient_org: f.recipient_org, subject: f.subject, signatory_id: f.signatory_id,
    };
    const miss = {};
    Object.entries(required).forEach(([k, v]) => { if (!String(v || '').trim()) miss[k] = true; });
    if (Object.keys(miss).length) {
      setMissing(miss); setErr('Please fill the required fields marked with a red star.');
      const first = document.querySelector(`[data-field="${Object.keys(miss)[0]}"]`);
      if (first) { first.scrollIntoView({ behavior: 'smooth', block: 'center' }); const inp2 = first.querySelector('input,select,textarea'); if (inp2) inp2.focus(); }
      return;
    }
    setMissing({}); setBusy(true); setErr('');
    try {
      const payload = { ...f, year: new Date(f.letter_date).getFullYear() };
      const res = existing ? await edrmsApi.put(`/outgoing/${existing.id}`, payload) : await edrmsApi.post('/outgoing', payload);
      onSaved(res.id || existing?.id);
    } catch (e) { setErr(e.error || 'Failed to save.'); }
    setBusy(false);
  };
  const star = <span style={{ color: '#DC2626' }}> *</span>;
  const fieldStyle = (k) => ({ ...inp, ...(missing[k] ? { borderColor: '#DC2626', background: '#FEF2F2' } : {}) });

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0, flex: 1 }}>{existing ? 'Edit Letter' : 'New Outgoing Letter'}</h3>
        <button onClick={onClose} style={btn('#F3F4F6', '#111827')}>Cancel</button>
      </div>
      {ref && !existing && <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>Reference to be assigned: <b>{ref.reference}</b></div>}
      {err && <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', color: '#B91C1C', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div data-field="category"><label style={lbl}>Category{star}</label>
          <select style={fieldStyle('category')} value={f.category} onChange={e => set('category', e.target.value)}>
            <option value="company">Company (SCT/n/year)</option>
            <option value="ceo">CEO (CEO-OUT-year-nnn)</option>
          </select></div>
        <div data-field="language"><label style={lbl}>Language{star}</label>
          <select style={fieldStyle('language')} value={f.language} onChange={e => set('language', e.target.value)}>
            <option value="en">English</option><option value="ar">Arabic</option>
          </select></div>
        <div data-field="letter_date"><label style={lbl}>Letter Date{star}</label>
          <input type="date" style={fieldStyle('letter_date')} value={f.letter_date} onChange={e => set('letter_date', e.target.value)} /></div>
      </div>

      <div style={{ marginBottom: 12, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
        <input type="checkbox" id="conf" checked={!!f.confidential} onChange={e => set('confidential', e.target.checked)} />
        <label htmlFor="conf" style={{ fontSize: 13 }}><b>Confidential</b> — generate the letter without typing the body in the system (metadata only).</label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div data-field="recipient_honorific"><label style={lbl}>Honorific{star}</label><input style={fieldStyle('recipient_honorific')} value={f.recipient_honorific ?? ''} onChange={e => set('recipient_honorific', e.target.value)} placeholder="الفاضل" /></div>
        <div data-field="recipient_name"><label style={lbl}>Recipient Name{star}</label><input style={fieldStyle('recipient_name')} value={f.recipient_name || ''} onChange={e => set('recipient_name', e.target.value)} /></div>
        <div><label style={lbl}>Recipient Title (optional)</label><input style={inp} value={f.recipient_title || ''} onChange={e => set('recipient_title', e.target.value)} /></div>
      </div>
      <div data-field="recipient_org" style={{ marginBottom: 12 }}><label style={lbl}>Recipient Organization (الجهة){star}</label><input style={fieldStyle('recipient_org')} value={f.recipient_org || ''} onChange={e => set('recipient_org', e.target.value)} /></div>
      {f.language === 'ar' && (
        <RecipientSimulator
          honorific={(f.recipient_honorific && f.recipient_honorific.trim()) || 'الفاضل'}
          name={f.recipient_name || ''}
          title={f.recipient_title || ''}
          org={f.recipient_org || ''}
          set={set}
        />
      )}
      <div data-field="subject" style={{ marginBottom: 12 }}><label style={lbl}>Subject (الموضوع){star}</label><input style={fieldStyle('subject')} value={f.subject || ''} onChange={e => set('subject', e.target.value)} /></div>

      {!f.confidential && (
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Body (محتوى الرسالة)</label>
          <textarea style={{ ...inp, minHeight: 160, resize: 'vertical', direction: f.language === 'ar' ? 'rtl' : 'ltr' }} value={f.body || ''} onChange={e => set('body', e.target.value)} placeholder={f.language === 'ar' ? 'اكتب نص الرسالة هنا…' : 'Write the letter body here…'} />
        </div>
      )}

      <div data-field="signatory_id" style={{ marginBottom: 12 }}>
        <label style={lbl}>Signatory — who the letter is from{star}</label>
        <select style={fieldStyle('signatory_id')} value={f.signatory_id || ''} onChange={e => set('signatory_id', e.target.value)}>
          <option value="">— Select signatory —</option>
          {signatories.map(s => <option key={s.id} value={s.id}>{(f.language === 'ar' ? (s.name_ar || s.name_en) : (s.name_en || s.name_ar))} — {(f.language === 'ar' ? (s.title_ar || s.title_en) : (s.title_en || s.title_ar)) || ''}</option>)}
        </select>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>Manage the list in Administration → Signatories.</div>
      </div>

      <div style={{ marginBottom: 12, padding: '10px 12px', background: '#FAFAF8', borderRadius: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!f.include_stamp} onChange={e => set('include_stamp', e.target.checked)} />
          <b>Include stamp</b> on the letter (off by default)
        </label>
        {f.include_stamp && (
          <select style={{ ...inp, marginTop: 8 }} value={f.stamp_id || ''} onChange={e => set('stamp_id', e.target.value)}>
            <option value="">— Choose stamp —</option>
            {stamps.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>

      <div style={{ marginBottom: 16, padding: '10px 12px', background: '#FAFAF8', borderRadius: 8 }}>
        <label style={{ ...lbl, marginBottom: 8, display: 'block' }}>CC / نسخة إلى (optional)</label>
        <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1fr 1fr', gap: 12, marginBottom: 10 }}>
          <div><label style={{ ...lbl, fontSize: 11 }}>CC Honorific</label><input style={inp} value={f.cc_honorific ?? ''} onChange={e => set('cc_honorific', e.target.value)} placeholder="الفاضل" /></div>
          <div><label style={{ ...lbl, fontSize: 11 }}>CC Name</label><input style={inp} value={f.cc_name || ''} onChange={e => set('cc_name', e.target.value)} placeholder={f.language === 'ar' ? 'اسم المرسل إليه نسخة' : 'CC recipient name'} /></div>
          <div><label style={{ ...lbl, fontSize: 11 }}>CC Title (optional)</label><input style={inp} value={f.cc_title || ''} onChange={e => set('cc_title', e.target.value)} placeholder={f.language === 'ar' ? 'المنصب أو الصفة' : 'CC title'} /></div>
        </div>
        <div><label style={{ ...lbl, fontSize: 11 }}>CC Organization (optional)</label><input style={inp} value={f.cc_org || ''} onChange={e => set('cc_org', e.target.value)} placeholder={f.language === 'ar' ? 'الجهة (اختياري)' : 'CC organization'} /></div>
        {f.language === 'ar' && (
          <CcSimulator honorific={(f.cc_honorific && f.cc_honorific.trim()) || 'الفاضل'} name={f.cc_name || ''} title={f.cc_title || ''} org={f.cc_org || ''} set={set} />
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Attachment / مرفق (optional)</label>
        <input style={inp} value={f.attachment || ''} onChange={e => set('attachment', e.target.value)} placeholder={f.language === 'ar' ? 'يظهر سطر "مرفق:" فقط عند التعبئة' : 'Shows a "مرفق:" line only if filled'} />
      </div>

      <button onClick={save} disabled={busy} style={{ ...btn(), width: '100%' }}>{busy ? 'Saving…' : (existing ? 'Save Changes' : 'Create Draft')}</button>
    </div>
  );
}

function OutgoingDetail({ id, me, onBack, lockPw }) {
  const [r, setR] = useState(null);
  const [members, setMembers] = useState([]);
  const [stamps, setStamps] = useState([]);
  const [editing, setEditing] = useState(false);
  const [reviewerIds, setReviewerIds] = useState([]);
  const [signId, setSignId] = useState('');
  const [stampId, setStampId] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const lockHeaders = lockPw ? { 'x-edrms-lock': lockPw } : {};
  const load = useCallback(() => edrmsApi.get(`/outgoing/${id}`, { headers: lockHeaders }).then(setR).catch(() => {}), [id, lockPw]);
  useEffect(() => { load(); edrmsApi.get('/members').then(setMembers).catch(() => {}); edrmsApi.get('/stamps').then(setStamps).catch(() => {}); }, [load]);
  useLiveRefresh(load);

  const act = async (fn) => { setBusy(true); setErr(''); try { await fn(); load(); } catch (e) { setErr(e.error || 'Failed'); } setBusy(false); };
  const token = localStorage.getItem('flowup_token');
  const [dl, setDl] = useState('');

  const download = async (format) => {
    setDl(format); setErr('');
    try {
      const resp = await fetch(`/api/edrms/outgoing/${id}/file?format=${format}&token=${token}`);
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        setErr(j.error || `Could not produce the ${format === 'pdf' ? 'PDF' : 'Word'} file.`);
        setDl(''); return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(r.reference || 'letter').replace(/[\/\\]/g, '-')}.${format === 'pdf' ? 'pdf' : 'docx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      setErr(`Could not download the ${format === 'pdf' ? 'PDF' : 'Word'} file.`);
    }
    setDl('');
  };

  if (!r) return <div style={{ color: '#6B7280' }}>Loading…</div>;
  if (editing) return <OutgoingForm me={me} existing={r} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />;

  const isCreator = r.created_by === me.id;
  const isReviewer = r.reviewer_id === me.id || (() => { try { return JSON.parse(r.reviewer_ids || '[]').includes(me.id); } catch (e) { return false; } })();
  const isSignatory = r.signatory_id === me.id;
  const canEdit = (['draft', 'returned'].includes(r.status) && isCreator) || me.is_admin;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <button onClick={onBack} style={{ ...btn('#F3F4F6', '#111827'), padding: '6px 12px' }}>← Back</button>
        <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0, flex: 1 }}>{r.reference} <Pill meta={OUT_STATUS[r.status]} /></h3>
        <button onClick={() => download('word')} disabled={dl === 'word'} style={{ ...btn('#2563EB'), border: 'none' }}>{dl === 'word' ? 'Preparing…' : '⬇ Word'}</button>
      </div>

      {err && <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', color: '#B91C1C', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 16, alignItems: 'start' }}>
        <div style={card}>
          {[['Recipient', `${r.recipient_title || ''} ${r.recipient_name || ''}`], ['Organization', r.recipient_org], ['Subject', r.subject],
            ['Language', r.language === 'ar' ? 'Arabic' : 'English'], ['Confidential', r.confidential ? 'Yes (metadata only)' : 'No'],
            ['Date', fmtDate(r.letter_date)], ['Hijri', r.hijri_date], ['Signatory', `${r.signatory_name || '—'}${r.signatory_title ? ' · ' + r.signatory_title : ''}`],
            ['CC', r.cc]].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', padding: '7px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
              <span style={{ width: 130, color: '#9CA3AF' }}>{k}</span><span style={{ flex: 1, fontWeight: 600 }}>{v || '—'}</span>
            </div>
          ))}
          {!r.confidential && r.body && (
            <div style={{ marginTop: 12 }}>
              <div style={{ ...lbl }}>Body</div>
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6, direction: r.language === 'ar' ? 'rtl' : 'ltr' }}>{r.body}</div>
            </div>
          )}
        </div>

        {/* Actions panel */}
        <div style={{ ...card }}>
          <div style={lbl}>Actions</div>
          {canEdit && <button onClick={() => setEditing(true)} style={{ ...btn('#F3F4F6', '#111827'), width: '100%', marginBottom: 8 }}>✎ Edit</button>}
          <LockManager kind="outgoing" id={r.id} locked={r.is_locked} lockPw={lockPw} onChanged={load} />

          {['draft', 'returned'].includes(r.status) && (isCreator || me.is_admin) && (
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Send for review — pick one or more reviewers</label>
              <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 8px', marginBottom: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '3px 0', fontWeight: 700 }}>
                  <input type="checkbox" checked={reviewerIds.includes(me.id)} onChange={e => setReviewerIds(e.target.checked ? [...reviewerIds, me.id] : reviewerIds.filter(x => x !== me.id))} />
                  Review it myself ({me.full_name})
                </label>
                {members.filter(m => m.id !== me.id).map(m => (
                  <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
                    <input type="checkbox" checked={reviewerIds.includes(m.id)} onChange={e => setReviewerIds(e.target.checked ? [...reviewerIds, m.id] : reviewerIds.filter(x => x !== m.id))} />
                    {m.full_name}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 6 }}>Any one reviewer approving applies the stamp. Then send it to the signatory for signature.</div>
              <button onClick={() => act(() => edrmsApi.post(`/outgoing/${id}/submit`, { reviewer_ids: reviewerIds }))} disabled={busy || !reviewerIds.length} style={{ ...btn(), width: '100%' }}>Submit for Review</button>
            </div>
          )}

          {r.status === 'under_review' && (isReviewer || me.is_admin) && (
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Review decision</label>
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginBottom: 6 }}>Approving applies the stamp{r.include_stamp ? '' : ' (this letter has no stamp enabled)'} and moves it to the signatory step.</div>
              <textarea style={{ ...inp, minHeight: 44, marginBottom: 8 }} value={comment} onChange={e => setComment(e.target.value)} placeholder="Comment (optional)…" />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => act(() => edrmsApi.post(`/outgoing/${id}/review`, { decision: 'approve', comment }))} disabled={busy} style={{ ...btn('#16A34A'), flex: 1 }}>Approve (apply stamp)</button>
                <button onClick={() => act(() => edrmsApi.post(`/outgoing/${id}/review`, { decision: 'return', comment }))} disabled={busy} style={{ ...btn('#F3F4F6', '#DC2626'), flex: 1 }}>Return</button>
              </div>
            </div>
          )}

          {r.status === 'approved' && (isCreator || me.is_admin) && (
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Send for signature</label>
              <select style={{ ...inp, marginBottom: 8 }} value={signId} onChange={e => setSignId(e.target.value)}>
                <option value="">— Choose signatory —</option>
                <option value={me.id}>Myself ({me.full_name})</option>
                {members.filter(m => m.id !== me.id).map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
              </select>
              <button onClick={() => act(() => edrmsApi.post(`/outgoing/${id}/request-signature`, { signatory_id: signId }))} disabled={busy || !signId} style={{ ...btn('#7C3AED'), width: '100%' }}>Send for Signature</button>
            </div>
          )}

          {r.status === 'pending_signature' && (isSignatory || me.is_admin) && (
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Sign the letter</label>
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginBottom: 8 }}>Signing applies the signatory's signature and issues the letter.</div>
              <button onClick={() => act(() => edrmsApi.post(`/outgoing/${id}/sign`, {}))} disabled={busy} style={{ ...btn('#16A34A'), width: '100%' }}>Sign & Issue</button>
            </div>
          )}

          {r.status === 'issued' && <button onClick={() => act(() => edrmsApi.post(`/outgoing/${id}/archive`, {}))} disabled={busy} style={{ ...btn('#0D9488'), width: '100%', marginBottom: 8 }}>Archive</button>}

          {!['issued', 'archived', 'cancelled'].includes(r.status) && (isCreator || me.is_admin) && (
            <button onClick={() => act(() => edrmsApi.post(`/outgoing/${id}/cancel`, {}))} disabled={busy} style={{ ...btn('#F3F4F6', '#DC2626'), width: '100%', marginBottom: 8 }}>Cancel Letter</button>
          )}
          {me.is_admin && <button onClick={() => act(() => edrmsApi.delete(`/outgoing/${id}`).then(onBack))} style={{ ...btn('#DC2626'), width: '100%' }}>Delete Permanently</button>}
        </div>

        {/* History window (below Actions) */}
        <HistoryWindow history={r.history || []} statusMap={OUT_STATUS} />
      </div>
    </div>
  );
}

function HistoryWindow({ history, statusMap }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const types = [['all', 'All'], ['downloaded', 'Downloads'], ['submitted', 'Submitted'], ['approved', 'Approved'], ['returned', 'Returned'], ['signed', 'Signed'], ['issued', 'Issued']];
  const rows = (history || []).filter(h => filter === 'all' ? true : h.action === filter);
  return (
    <div style={{ ...card, marginTop: 16, gridColumn: '2' }}>
      <button onClick={() => setOpen(o => !o)} style={{ ...btn('#F3F4F6', '#111827'), width: '100%', display: 'flex', justifyContent: 'space-between' }}>
        <span>🕑 History & audit log ({(history || []).length})</span><span>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          <select style={{ ...inp, marginBottom: 8, fontSize: 12 }} value={filter} onChange={e => setFilter(e.target.value)}>
            {types.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {rows.length === 0 && <div style={{ fontSize: 12, color: '#9CA3AF', padding: '6px 0' }}>No entries.</div>}
            {rows.map(h => (
              <div key={h.id} style={{ fontSize: 12, color: '#6B7280', padding: '5px 0', borderBottom: '1px solid #F9FAFB' }}>
                <b style={{ color: h.action === 'downloaded' ? '#2563EB' : '#374151' }}>{h.action === 'downloaded' ? '⬇ Downloaded' : ((statusMap[h.to_status]?.label) || h.action)}</b> · {h.user_name} · {new Date(h.created_at).toLocaleString('en-GB')}{h.comment && h.action !== 'downloaded' ? ` — ${h.comment}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Incoming ─────────────────────────────────────────────────────────────────
function IncomingSection({ year, me }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [openPw, setOpenPw] = useState(null);
  const [unlockFor, setUnlockFor] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback((silent) => edrmsApi.get(`/incoming?year=${year}`).then(setRows).catch(() => setRows([])), [year]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(true));
  useEffect(() => { if (open || creating) document.getElementById('edrms-scroll')?.scrollTo(0, 0); }, [open, creating]);

  const openRow = (r) => { if (r.is_locked) { setUnlockFor(r); } else { setOpenPw(null); setOpen(r.id); } };

  if (creating) return <IncomingForm me={me} onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); load(); setOpen(id); }} />;
  if (open) return <IncomingDetail id={open} me={me} lockPw={openPw} onBack={() => { setOpen(null); setOpenPw(null); load(); document.getElementById('edrms-scroll')?.scrollTo(0, 0); }} />;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0, flex: 1 }}>Incoming Letters (الوارد)</h3>
        <button onClick={() => setCreating(true)} style={btn()}>+ Register Incoming</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
          <thead><tr style={{ textAlign: 'left', fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase' }}>
            {['Reference', 'Received', 'Sender', 'Subject', 'Directed To', 'Status'].map(h => <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid #E5E7EB' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} style={{ padding: 20, color: '#9CA3AF' }}>No incoming letters yet.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} onClick={() => openRow(r)} style={{ cursor: 'pointer', borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '10px', fontWeight: 700 }}>{r.is_locked ? '🔒 ' : ''}{r.reference}</td>
                <td style={{ padding: '10px', fontSize: 12.5 }}>{r.is_locked ? '—' : fmtDate(r.received_date)}</td>
                <td style={{ padding: '10px', fontSize: 12.5 }}>{r.is_locked ? <span style={{ color: '#9CA3AF' }}>— locked —</span> : <>{r.sender_name}<div style={{ color: '#9CA3AF', fontSize: 11 }}>{r.sender_org}</div></>}</td>
                <td style={{ padding: '10px', fontSize: 12.5 }}>{r.is_locked ? '' : r.subject}</td>
                <td style={{ padding: '10px', fontSize: 12.5 }}>{r.is_locked ? '—' : (r.directed_to || '—')}</td>
                <td style={{ padding: '10px' }}><Pill meta={IN_STATUS[r.status]} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {unlockFor && <UnlockModal kind="incoming" item={unlockFor} onClose={() => setUnlockFor(null)}
        onUnlocked={(pw) => { setUnlockFor(null); setOpenPw(pw); setOpen(unlockFor.id); }} />}
    </div>
  );
}

function IncomingAttachment({ id, current, onChange }) {
  const [name, setName] = useState(current || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await edrmsApi.postForm(`/incoming/${id}/attachment`, fd);
      setName(r.attachment_name || file.name);
      onChange && onChange(r.attachment_name || file.name);
    } catch (e) { setErr(e.error || 'Upload failed.'); }
    setBusy(false);
  };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input type="file" onChange={e => upload(e.target.files[0])} disabled={busy} style={{ fontSize: 12.5 }} />
        {busy && <span style={{ fontSize: 12, color: '#9CA3AF' }}>Uploading…</span>}
      </div>
      {name && <div style={{ fontSize: 12, marginTop: 6 }}>Current: <a href={`/api/edrms/incoming/${id}/attachment`} target="_blank" rel="noreferrer" style={{ color: '#2563EB' }}>{name}</a></div>}
      {err && <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 4 }}>{err}</div>}
    </div>
  );
}

function IncomingForm({ me, onClose, onSaved, existing }) {
  const initInc = existing
    ? { ...existing, received_date: (existing.received_date ? String(existing.received_date).slice(0, 10) : new Date().toISOString().slice(0, 10)) }
    : { category: 'company', received_via: 'email', received_date: new Date().toISOString().slice(0, 10), status: 'registered', language: 'en' };
  const [f, setF] = useState(initInc);
  const [ref, setRef] = useState(null);
  const [depts, setDepts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => { edrmsApi.get('/departments').then(setDepts).catch(() => {}); }, []);
  useEffect(() => {
    if (existing) return;
    edrmsApi.get(`/next-number?direction=incoming&category=${f.category}&year=${new Date(f.received_date).getFullYear()}`).then(setRef).catch(() => {});
  }, [f.category, f.received_date, existing]);

  const save = async () => {
    // All core fields are mandatory: Received date, Sender, Subject, Directed To, Status.
    if (!f.received_date) { setErr('Received date is required.'); return; }
    if (!f.sender_name && !f.sender_org) { setErr('Sender name or organization is required.'); return; }
    if (!f.subject) { setErr('Subject is required.'); return; }
    if (!f.directed_to) { setErr('Directed To is required.'); return; }
    if (!f.status) { setErr('Status is required.'); return; }
    setBusy(true); setErr('');
    try {
      const payload = { ...f, year: new Date(f.received_date).getFullYear() };
      const res = existing ? await edrmsApi.put(`/incoming/${existing.id}`, payload) : await edrmsApi.post('/incoming', payload);
      onSaved(res.id || existing?.id);
    } catch (e) { setErr(e.error || 'Failed to save.'); }
    setBusy(false);
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0, flex: 1 }}>{existing ? 'Edit Incoming' : 'Register Incoming Letter'}</h3>
        <button onClick={onClose} style={btn('#F3F4F6', '#111827')}>Cancel</button>
      </div>
      {ref && !existing && <div style={{ background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>Reference to be assigned: <b>{ref.reference}</b></div>}
      {err && <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', color: '#B91C1C', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div><label style={lbl}>Category</label>
          <select style={inp} value={f.category} onChange={e => set('category', e.target.value)}>
            <option value="company">Company (IN-SCT-year-nnn)</option>
            <option value="ceo">CEO (CEO-IN-year-nnn)</option>
          </select></div>
        <div><label style={lbl}>Received Via</label>
          <select style={inp} value={f.received_via} onChange={e => set('received_via', e.target.value)}>
            {Object.entries(RECEIVED_VIA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><label style={lbl}>Received Date</label><input type="date" style={inp} value={f.received_date} onChange={e => set('received_date', e.target.value)} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div><label style={lbl}>Sender Name <span style={{ color: '#DC2626' }}>*</span></label><input style={inp} value={f.sender_name || ''} onChange={e => set('sender_name', e.target.value)} /></div>
        <div><label style={lbl}>Sender Organization</label><input style={inp} value={f.sender_org || ''} onChange={e => set('sender_org', e.target.value)} /></div>
      </div>
      <div style={{ marginBottom: 12 }}><label style={lbl}>Subject <span style={{ color: '#DC2626' }}>*</span></label><input style={inp} value={f.subject || ''} onChange={e => set('subject', e.target.value)} /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div><label style={lbl}>Directed To <span style={{ color: '#DC2626' }}>*</span></label>
          <select style={inp} value={f.directed_to || ''} onChange={e => set('directed_to', e.target.value)}>
            <option value="">— Select department —</option>
            {depts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select></div>
        <div><label style={lbl}>Status <span style={{ color: '#DC2626' }}>*</span></label>
          <select style={inp} value={f.status || 'registered'} onChange={e => set('status', e.target.value)}>
            {Object.entries(IN_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div><label style={lbl}>Language</label>
          <select style={inp} value={f.language || 'en'} onChange={e => set('language', e.target.value)}>
            <option value="en">English</option>
            <option value="ar">Arabic / عربي</option>
          </select></div>
        <div><label style={lbl}>Action (optional)</label>
          <select style={inp} value={f.action || ''} onChange={e => set('action', e.target.value)}>
            <option value="">— None —</option>
            {Object.entries(ACTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
      </div>
      <div style={{ marginBottom: 12 }}><label style={lbl}>Notes (optional)</label><textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={f.notes || ''} onChange={e => set('notes', e.target.value)} dir={f.language === 'ar' ? 'rtl' : 'ltr'} /></div>
      <div style={{ marginBottom: 12 }}><label style={lbl}>Attachment link (optional) — e.g. OneDrive/SharePoint URL</label><input style={inp} value={f.attachment_link || ''} onChange={e => set('attachment_link', e.target.value)} placeholder="https://…" /></div>
      {existing ? (
        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Attachment file (optional)</label>
          <IncomingAttachment id={existing.id} current={f.attachment_name} onChange={n => set('attachment_name', n)} />
        </div>
      ) : (
        <div style={{ marginBottom: 16, fontSize: 11.5, color: '#9CA3AF' }}>You can attach a file after registering (open the record and use “Attach file”), or paste a link above now.</div>
      )}
      <button onClick={save} disabled={busy} style={{ ...btn(), width: '100%' }}>{busy ? 'Saving…' : (existing ? 'Save Changes' : 'Register')}</button>
    </div>
  );
}

function IncomingDetail({ id, me, onBack, lockPw }) {
  const [r, setR] = useState(null);
  const [depts, setDepts] = useState([]);
  const [members, setMembers] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [editing, setEditing] = useState(false);
  const [directedTo, setDirectedTo] = useState('');
  const [assignUser, setAssignUser] = useState('');
  const [action, setAction] = useState('');
  const [relOut, setRelOut] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const lockHeaders = lockPw ? { 'x-edrms-lock': lockPw } : {};
  const load = useCallback(() => edrmsApi.get(`/incoming/${id}`, { headers: lockHeaders }).then(r => { setR(r); setRelOut(r.related_outgoing_id || ''); }).catch(() => {}), [id, lockPw]);
  useEffect(() => {
    load();
    edrmsApi.get('/departments').then(setDepts).catch(() => {});
    edrmsApi.get('/members').then(setMembers).catch(() => {});
    edrmsApi.get('/outgoing').then(setOutgoing).catch(() => {});
  }, [load]);
  useLiveRefresh(load);

  const act = async (fn) => { setBusy(true); setErr(''); try { await fn(); load(); } catch (e) { setErr(e.error || 'Failed'); } setBusy(false); };
  if (!r) return <div style={{ color: '#6B7280' }}>Loading…</div>;
  if (editing) return <IncomingForm me={me} existing={r} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <button onClick={onBack} style={{ ...btn('#F3F4F6', '#111827'), padding: '6px 12px' }}>← Back</button>
        <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0, flex: 1 }}>{r.reference} <Pill meta={IN_STATUS[r.status]} /></h3>
      </div>
      {err && <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', color: '#B91C1C', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 16, alignItems: 'start' }}>
        <div style={card}>
          {[['Received Via', RECEIVED_VIA[r.received_via] || r.received_via], ['Received', fmtDate(r.received_date)], ['Language', r.language === 'ar' ? 'Arabic' : 'English'], ['Sender', r.sender_name], ['Organization', r.sender_org],
            ['Subject', r.subject], ['Directed To', r.directed_to], ['Assigned To', r.assigned_user_name], ['Action', ACTIONS[r.action] || r.action]].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', padding: '7px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
              <span style={{ width: 130, color: '#9CA3AF' }}>{k}</span><span style={{ flex: 1, fontWeight: 600 }}>{v || '—'}</span>
            </div>
          ))}
          {r.attachment_link && (
            <div style={{ display: 'flex', padding: '7px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
              <span style={{ width: 130, color: '#9CA3AF' }}>Link</span>
              <a href={r.attachment_link} target="_blank" rel="noreferrer" style={{ flex: 1, fontWeight: 600, color: '#2563EB', wordBreak: 'break-all' }}>{r.attachment_link} ↗</a>
            </div>
          )}
          {r.attachment_name && (
            <div style={{ display: 'flex', padding: '7px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
              <span style={{ width: 130, color: '#9CA3AF' }}>Attachment</span>
              <a href={`/api/edrms/incoming/${id}/attachment`} target="_blank" rel="noreferrer" style={{ flex: 1, fontWeight: 600, color: '#2563EB' }}>⬇ {r.attachment_name}</a>
            </div>
          )}
          {r.notes && (
            <div style={{ padding: '7px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
              <div style={{ color: '#9CA3AF', marginBottom: 3 }}>Notes</div>
              <div style={{ fontWeight: 500, whiteSpace: 'pre-wrap', direction: r.language === 'ar' ? 'rtl' : 'ltr' }}>{r.notes}</div>
            </div>
          )}
          {r.related_outgoing && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#FFF7ED', borderRadius: 8, fontSize: 13 }}>
              <b>Related Outgoing reply:</b> {r.related_outgoing.reference} — {r.related_outgoing.subject}
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <div style={lbl}>History</div>
            {(r.history || []).map(h => (
              <div key={h.id} style={{ fontSize: 12, color: '#6B7280', padding: '4px 0', borderBottom: '1px solid #F9FAFB' }}>
                <b style={{ color: '#374151' }}>{(IN_STATUS[h.to_status]?.label) || h.action}</b> · {h.user_name} · {fmtDate(h.created_at)} {h.comment ? `— ${h.comment}` : ''}
              </div>
            ))}
          </div>
        </div>

        <div style={card}>
          <div style={lbl}>Actions</div>
          <button onClick={() => setEditing(true)} style={{ ...btn('#F3F4F6', '#111827'), width: '100%', marginBottom: 10 }}>✎ Edit</button>
          <LockManager kind="incoming" id={r.id} locked={r.is_locked} lockPw={lockPw} onChanged={load} />

          <label style={lbl}>Route / Assign</label>
          <select style={{ ...inp, marginBottom: 6 }} value={directedTo} onChange={e => setDirectedTo(e.target.value)}>
            <option value="">— Directed to (department) —</option>
            <option value="Company">Company</option><option value="CEO">CEO</option><option value="Board of Directors">Board of Directors</option><option value="Audit Committee">Audit Committee</option>
            {depts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
          <select style={{ ...inp, marginBottom: 6 }} value={assignUser} onChange={e => setAssignUser(e.target.value)}>
            <option value="">— Assign to person (optional) —</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
          </select>
          <button onClick={() => act(() => edrmsApi.post(`/incoming/${id}/assign`, { directed_to: directedTo, assigned_user_id: assignUser || undefined }))} disabled={busy || (!directedTo && !assignUser)} style={{ ...btn('#2563EB'), width: '100%', marginBottom: 12 }}>Assign</button>

          <label style={lbl}>Action</label>
          <select style={{ ...inp, marginBottom: 6 }} value={action} onChange={e => setAction(e.target.value)}>
            <option value="">— Choose action —</option>
            {Object.entries(ACTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={() => act(() => edrmsApi.post(`/incoming/${id}/action`, { action }))} disabled={busy || !action} style={{ ...btn('#D97706'), width: '100%', marginBottom: 12 }}>Record Action</button>

          <button onClick={() => act(() => edrmsApi.post(`/incoming/${id}/close`, {}))} disabled={busy} style={{ ...btn('#16A34A'), width: '100%', marginBottom: 8 }}>Close Letter</button>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>To link a reply, open the outgoing letter and set its “Related Incoming Letter”. It will then appear here automatically.</div>
          {me.is_admin && <button onClick={() => act(() => edrmsApi.delete(`/incoming/${id}`).then(onBack))} style={{ ...btn('#DC2626'), width: '100%' }}>Delete</button>}
        </div>
      </div>
    </div>
  );
}

// ── Administration ───────────────────────────────────────────────────────────
function PageHead({ title, desc, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 18 }}>
      <div style={{ flex: 1 }}>
        <h2 style={{ fontSize: 19, fontWeight: 850, margin: 0 }}>{title}</h2>
        {desc && <div style={{ fontSize: 13, color: '#6B7280', marginTop: 3 }}>{desc}</div>}
      </div>
      {action}
    </div>
  );
}

function AdminSettings() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState('');
  const [diag, setDiag] = useState(null);
  useEffect(() => { edrmsApi.get('/settings').then(setS).catch(() => setS({})); edrmsApi.get('/diagnostics').then(setDiag).catch(() => setDiag(null)); }, []);
  if (!s) return <div style={{ color: '#6B7280' }}>Loading…</div>;
  const set = (k, v) => setS(p => ({ ...p, [k]: v }));
  const save = async () => { try { await edrmsApi.put('/settings', s); setMsg('Saved'); setTimeout(() => setMsg(''), 2000); } catch (e) { setMsg(e.error || 'Failed'); } };

  const numRow = (k, label, example) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid #F3F0EA' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 650, color: '#374151' }}>{label}</div>
        <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>Next will be <b style={{ color: ACCENT }}>{example(s[k] || '1')}</b></div>
      </div>
      <input type="number" min="1" style={{ ...inp, width: 90, textAlign: 'center' }} value={s[k] || ''} onChange={e => set(k, e.target.value)} />
    </div>
  );
  const yr = new Date().getFullYear();
  return (
    <div>
      <PageHead title="Numbering & Settings" desc="Reference numbering and organization details. Numbering resets automatically every year." />

      <div style={card}>
        <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 4 }}>Outgoing (الصادر)</div>
        {numRow('outgoing_company_start', 'Company series start', n => `${s.org_name || 'SCT'}/${n}/${yr}`)}
        {numRow('outgoing_ceo_start', 'CEO series start', n => `CEO-OUT-${yr}-${String(n).padStart(3, '0')}`)}
        <div style={{ fontSize: 13.5, fontWeight: 800, margin: '18px 0 4px' }}>Incoming (الوارد)</div>
        {numRow('incoming_company_start', 'Company series start', n => `IN-${s.org_name || 'SCT'}-${yr}-${String(n).padStart(3, '0')}`)}
        {numRow('incoming_ceo_start', 'CEO series start', n => `CEO-IN-${yr}-${String(n).padStart(3, '0')}`)}
      </div>

      <div style={card}>
        <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 10 }}>Organization</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div><label style={lbl}>Short name (used in references)</label><input style={inp} value={s.org_name || ''} onChange={e => set('org_name', e.target.value)} /></div>
          <div><label style={lbl}>Full name</label><input style={inp} value={s.org_name_full || ''} onChange={e => set('org_name_full', e.target.value)} /></div>
        </div>
      </div>

      <button onClick={save} style={btn()}>Save Settings</button>
      {msg && <span style={{ marginLeft: 12, fontSize: 13, color: '#16A34A', fontWeight: 700 }}>✓ {msg}</span>}

      <OneDriveSettings s={s} set={set} onSave={save} />

      <div style={{ ...card, marginTop: 18 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 4 }}>Maintenance</div>
        <div style={{ fontSize: 12.5, color: '#6B7280', marginBottom: 10 }}>
          Regenerate the Word and PDF files for all existing letters using the current template.
          Use this once after a template/format update so older letters match the new look.
          The letters' data is not changed — only the documents are rebuilt.
        </div>
        <Rebuilder />
      </div>
    </div>
  );
}

function OneDriveSettings({ s, set, onSave }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const enabled = s.onedrive_enabled === '1' || s.onedrive_enabled === 'true';
  const test = async () => {
    setTesting(true); setResult(null);
    try { await onSave(); const r = await edrmsApi.post('/onedrive/test', {}); setResult(r); }
    catch (e) { setResult({ ok: false, message: e.error || 'Failed' }); }
    setTesting(false);
  };
  const field = (k, label, ph, type = 'text') => (
    <div><label style={lbl}>{label}</label>
      <input type={type} style={inp} value={s[k] || ''} onChange={e => set(k, e.target.value)} placeholder={ph} autoComplete="off" /></div>
  );
  return (
    <div style={{ ...card, marginTop: 18 }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 4 }}>OneDrive / SharePoint integration</div>
      <div style={{ fontSize: 12.5, color: '#6B7280', marginBottom: 12 }}>
        Save approved letters (and incoming attachments) straight to a OneDrive/SharePoint folder via
        Microsoft Graph. Register an app in Azure (Microsoft Entra ID → App registrations), grant it
        <b> Files.ReadWrite.All</b> application permission with admin consent, then paste the values here.
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={e => set('onedrive_enabled', e.target.checked ? '1' : '0')} />
        <b>Enable OneDrive saving</b>
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        {field('onedrive_tenant_id', 'Directory (tenant) ID', '00000000-0000-0000-0000-000000000000')}
        {field('onedrive_client_id', 'Application (client) ID', '00000000-0000-0000-0000-000000000000')}
        {field('onedrive_client_secret', 'Client secret (value)', '••••••••', 'password')}
        {field('onedrive_drive_id', 'Drive ID (target library)', 'b!xxxxxxxx…')}
        {field('onedrive_outgoing_folder', 'Outgoing folder path', 'EDRMS/Outgoing')}
        {field('onedrive_incoming_folder', 'Incoming folder path', 'EDRMS/Incoming')}
      </div>
      <button onClick={onSave} style={btn()}>Save OneDrive Settings</button>
      <button onClick={test} disabled={testing} style={{ ...btn('#F3F0EA', '#374151'), marginLeft: 10 }}>
        {testing ? 'Testing…' : 'Save & Test connection'}
      </button>
      {result && (
        <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, fontSize: 12.5,
          background: result.ok ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${result.ok ? '#BBF7D0' : '#FECACA'}`,
          color: result.ok ? '#15803D' : '#B91C1C' }}>
          {result.ok ? '✓ ' : '✗ '}{result.message}
        </div>
      )}
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8 }}>
        Note: this connects to Microsoft live, so it only works once deployed with valid Azure
        credentials. The connection test must be run on the server, not in preview.
      </div>
    </div>
  );
}

function Rebuilder() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true); setResult(null);
    try { const r = await edrmsApi.post('/rebuild-all', {}); setResult(r); }
    catch (e) { setResult({ error: e.error || 'Failed' }); }
    setBusy(false);
  };
  return (
    <div>
      <button onClick={run} disabled={busy} style={{ ...btn('#F3F0EA', '#374151') }}>
        {busy ? 'Rebuilding all letters…' : '↻ Rebuild all letters with current template'}
      </button>
      {result && !result.error && <span style={{ marginLeft: 12, fontSize: 13, color: '#16A34A', fontWeight: 700 }}>✓ Rebuilt {result.rebuilt} of {result.total}{result.failed ? ` (${result.failed} failed)` : ''}</span>}
      {result && result.error && <span style={{ marginLeft: 12, fontSize: 13, color: '#DC2626' }}>{result.error}</span>}
    </div>
  );
}

function AdminRoles() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const load = () => edrmsApi.get('/roles').then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const setRole = async (uid, role) => { try { await edrmsApi.put(`/roles/${uid}`, { edrms_role: role }); load(); } catch (e) {} };
  const roleBadge = (r) => r === 'admin' ? { t: 'Admin', c: ACCENT, b: 'rgba(180,83,9,0.12)' } : r === 'member' ? { t: 'Member', c: '#2563EB', b: 'rgba(37,99,235,0.12)' } : { t: 'No access', c: '#9CA3AF', b: '#F3F4F6' };
  const filtered = rows.filter(u => !q || u.full_name?.toLowerCase().includes(q.toLowerCase()) || u.email?.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <PageHead title="Members & Roles" desc="Assign EDRMS Admin or Member. Admins configure everything; members create and handle letters." />
      <input style={{ ...inp, maxWidth: 280, marginBottom: 14 }} placeholder="Search people…" value={q} onChange={e => setQ(e.target.value)} />
      <div style={card}>
        {filtered.map(u => { const b = roleBadge(u.edrms_role); return (
          <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #F3F0EA' }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#F3F0EA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#6B7280', fontSize: 13 }}>{(u.full_name || '?')[0]}</div>
            <div style={{ flex: 1 }}><div style={{ fontSize: 13.5, fontWeight: 650 }}>{u.full_name}</div><div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{u.email}</div></div>
            <span style={{ fontSize: 11, fontWeight: 700, color: b.c, background: b.b, padding: '3px 10px', borderRadius: 999 }}>{b.t}</span>
            <select value={u.edrms_role} onChange={e => setRole(u.id, e.target.value)} style={{ ...inp, width: 130 }}>
              <option value="none">No access</option><option value="member">Member</option><option value="admin">Admin</option>
            </select>
          </div>
        ); })}
        {filtered.length === 0 && <div style={{ padding: 16, color: '#9CA3AF', fontSize: 13 }}>No people found.</div>}
      </div>
    </div>
  );
}

function AdminSignatories() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [sigBust, setSigBust] = useState(0);
  const load = () => edrmsApi.get('/signatories').then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!modal.name_en || !modal.name_ar || !modal.title_en || !modal.title_ar) { setModal({ ...modal, _miss: true }); return; }
    try {
      if (modal.id) await edrmsApi.put(`/signatories/${modal.id}`, modal);
      else await edrmsApi.post('/signatories', modal);
      setModal(null); load();
    } catch (e) {}
  };
  const remove = async (id) => { try { await edrmsApi.delete(`/signatories/${id}`); load(); } catch (e) {} };
  const token = localStorage.getItem('flowup_token');
  const uploadSignature = async (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    const resp = await fetch(`/api/edrms/signatories/${id}/signature?token=${token}`, { method: 'POST', body: fd });
    if (resp.ok) { setSigBust(Date.now()); load(); }
    else { const j = await resp.json().catch(() => ({})); alert(j.error || 'Upload failed'); }
  };
  const removeSignature = async (id) => { try { await edrmsApi.delete(`/signatories/${id}/signature`); setSigBust(Date.now()); load(); } catch (e) {} };
  return (
    <div>
      <PageHead title="Signatories" desc="The people a letter can be 'from'. Pick one when creating a letter; the name and title fill in per language. Upload each person's signature PNG so it appears below their name once the letter is issued." action={<button onClick={() => setModal({ name_en: '', name_ar: '', title_en: '', title_ar: '' })} style={btn()}>+ Add Signatory</button>} />
      {rows.length === 0 && <div style={{ ...card, color: '#9CA3AF', fontSize: 13, textAlign: 'center', padding: 30 }}>No signatories yet.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
        {rows.map(s => (
          <div key={s.id} style={card}>
            <div style={{ fontSize: 14.5, fontWeight: 800 }}>{s.name_en}</div>
            <div style={{ fontSize: 13, color: '#6B7280' }}>{s.title_en}</div>
            <div style={{ fontSize: 14.5, fontWeight: 800, direction: 'rtl', marginTop: 6 }}>{s.name_ar}</div>
            <div style={{ fontSize: 13, color: '#6B7280', direction: 'rtl' }}>{s.title_ar}</div>
            <div style={{ marginTop: 10, fontSize: 12, color: s.signature_path ? '#16A34A' : '#9CA3AF', fontWeight: 600 }}>
              {s.signature_path ? '✓ Signature on file' : 'No signature uploaded'}
            </div>
            {s.signature_path && (
              <img alt="signature" src={`/api/edrms/signatories/${s.id}/signature?token=${token}&b=${sigBust}`} style={{ maxWidth: '100%', maxHeight: 60, marginTop: 6, objectFit: 'contain' }} />
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={() => setModal({ id: s.id, name_en: s.name_en || '', name_ar: s.name_ar || '', title_en: s.title_en || '', title_ar: s.title_ar || '', signature_path: s.signature_path })} style={{ ...btn('#F3F0EA', '#374151'), padding: '6px 14px', fontSize: 12.5, flex: 1 }}>Edit</button>
              <button onClick={() => remove(s.id)} style={{ ...btn('#FEF2F2', '#DC2626'), padding: '6px 14px', fontSize: 12.5 }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      {modal && (
        <Modal title={modal.id ? 'Edit Signatory' : 'Add Signatory'} onClose={() => setModal(null)} width={500}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Name (English) <span style={{ color: '#DC2626' }}>*</span></label><input style={{ ...inp, ...(modal._miss && !modal.name_en ? { borderColor: '#DC2626', background: '#FEF2F2' } : {}) }} value={modal.name_en} onChange={e => setModal({ ...modal, name_en: e.target.value })} autoFocus /></div>
            <div><label style={lbl}>Name (Arabic) <span style={{ color: '#DC2626' }}>*</span></label><input style={{ ...inp, direction: 'rtl', ...(modal._miss && !modal.name_ar ? { borderColor: '#DC2626', background: '#FEF2F2' } : {}) }} value={modal.name_ar} onChange={e => setModal({ ...modal, name_ar: e.target.value })} /></div>
            <div><label style={lbl}>Title (English) <span style={{ color: '#DC2626' }}>*</span></label><input style={{ ...inp, ...(modal._miss && !modal.title_en ? { borderColor: '#DC2626', background: '#FEF2F2' } : {}) }} value={modal.title_en} onChange={e => setModal({ ...modal, title_en: e.target.value })} placeholder="Executive Director" /></div>
            <div><label style={lbl}>Title (Arabic) <span style={{ color: '#DC2626' }}>*</span></label><input style={{ ...inp, direction: 'rtl', ...(modal._miss && !modal.title_ar ? { borderColor: '#DC2626', background: '#FEF2F2' } : {}) }} value={modal.title_ar} onChange={e => setModal({ ...modal, title_ar: e.target.value })} placeholder="المدير التنفيذي" /></div>
          </div>

          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #ECE9E3' }}>
            <label style={lbl}>Signature (PNG)</label>
            {!modal.id && <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>Save the signatory first, then re-open Edit to upload a signature.</div>}
            {modal.id && (
              <div>
                {modal.signature_path && (
                  <div style={{ marginBottom: 8 }}>
                    <img alt="signature" src={`/api/edrms/signatories/${modal.id}/signature?token=${token}&b=${sigBust}`} style={{ maxWidth: 200, maxHeight: 70, objectFit: 'contain', border: '1px solid #ECE9E3', borderRadius: 8, padding: 4 }} />
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="file" accept="image/png" onChange={async e => { const f = e.target.files[0]; if (f) { await uploadSignature(modal.id, f); setModal({ ...modal, signature_path: 'pending' }); } }} style={{ fontSize: 12.5 }} />
                  {modal.signature_path && <button onClick={async () => { await removeSignature(modal.id); setModal({ ...modal, signature_path: null }); }} style={{ ...btn('#FEF2F2', '#DC2626'), padding: '4px 12px', fontSize: 12 }}>Remove</button>}
                </div>
                <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 6 }}>Use a transparent-background PNG of the handwritten signature. It appears below the name once the letter is issued.</div>
              </div>
            )}
          </div>

          <button onClick={save} style={{ ...btn(), width: '100%', marginTop: 16 }}>{modal.id ? 'Save Changes' : 'Add Signatory'}</button>
        </Modal>
      )}
    </div>
  );
}

function AdminGroups() {
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [modal, setModal] = useState(null); // {id?,name,description,member_ids}
  const load = () => edrmsApi.get('/groups').then(setGroups).catch(() => setGroups([]));
  useEffect(() => { load(); edrmsApi.get('/members').then(setMembers).catch(() => {}); }, []);
  const save = async () => {
    if (!modal.name) return;
    try {
      if (modal.id) await edrmsApi.put(`/groups/${modal.id}`, modal);
      else await edrmsApi.post('/groups', modal);
      setModal(null); load();
    } catch (e) {}
  };
  const remove = async (id) => { try { await edrmsApi.delete(`/groups/${id}`); load(); } catch (e) {} };
  const toggle = (uid) => setModal(m => ({ ...m, member_ids: (m.member_ids || []).includes(uid) ? m.member_ids.filter(x => x !== uid) : [...(m.member_ids || []), uid] }));
  return (
    <div>
      <PageHead title="Groups" desc="Group people so they're notified together on correspondence." action={<button onClick={() => setModal({ name: '', description: '', member_ids: [] })} style={btn()}>+ New Group</button>} />
      {groups.length === 0 && <div style={{ ...card, color: '#9CA3AF', fontSize: 13, textAlign: 'center', padding: 30 }}>No groups yet. Create one to notify a set of people together.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {groups.map(g => (
          <div key={g.id} style={card}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 800 }}>{g.name}</div>
                {g.description && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{g.description}</div>}
              </div>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(180,83,9,0.1)', color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>{(g.members || []).length}</div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, margin: '10px 0' }}>
              {(g.members || []).map(m => <span key={m.id} style={{ fontSize: 11, background: '#F3F0EA', padding: '3px 9px', borderRadius: 999 }}>{m.full_name}</span>)}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setModal({ id: g.id, name: g.name, description: g.description || '', member_ids: (g.members || []).map(m => m.id) })} style={{ ...btn('#F3F0EA', '#374151'), padding: '6px 14px', fontSize: 12.5, flex: 1 }}>Edit</button>
              <button onClick={() => remove(g.id)} style={{ ...btn('#FEF2F2', '#DC2626'), padding: '6px 14px', fontSize: 12.5 }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      {modal && (
        <Modal title={modal.id ? 'Edit Group' : 'New Group'} onClose={() => setModal(null)} width={480}>
          <label style={lbl}>Group name <span style={{ color: '#DC2626' }}>*</span></label>
          <input style={{ ...inp, marginBottom: 12, ...(modal._miss ? { borderColor: '#DC2626', background: '#FEF2F2' } : {}) }} value={modal.name} onChange={e => setModal({ ...modal, name: e.target.value, _miss: false })} placeholder="e.g. Finance Reviewers" autoFocus />
          <label style={lbl}>Description (optional)</label>
          <input style={{ ...inp, marginBottom: 14 }} value={modal.description} onChange={e => setModal({ ...modal, description: e.target.value })} />
          <label style={lbl}>Members ({(modal.member_ids || []).length} selected)</label>
          <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, maxHeight: 240, overflowY: 'auto', marginBottom: 16 }}>
            {members.map(m => {
              const on = (modal.member_ids || []).includes(m.id);
              return (
                <div key={m.id} onClick={() => toggle(m.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #F9FAFB', background: on ? 'rgba(180,83,9,0.06)' : '#fff' }}>
                  <div style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${on ? ACCENT : '#D1D5DB'}`, background: on ? ACCENT : '#fff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>{on ? '✓' : ''}</div>
                  <span style={{ fontSize: 13 }}>{m.full_name}</span>
                </div>
              );
            })}
          </div>
          <button onClick={() => { if (!modal.name) { setModal({ ...modal, _miss: true }); return; } save(); }} style={{ ...btn(), width: '100%' }}>{modal.id ? 'Save Changes' : 'Create Group'}</button>
        </Modal>
      )}
    </div>
  );
}

function AdminDepartments() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const load = () => edrmsApi.get('/departments').then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const save = async () => { if (!modal.name) return; try { if (modal.id) await edrmsApi.put(`/departments/${modal.id}`, modal); else await edrmsApi.post('/departments', modal); setModal(null); load(); } catch (e) {} };
  const remove = async (id) => { try { await edrmsApi.delete(`/departments/${id}`); load(); } catch (e) {} };
  return (
    <div>
      <PageHead title="Departments / Sections" desc="Used to classify and route incoming letters." action={<button onClick={() => setModal({ name: '', name_ar: '' })} style={btn()}>+ Add Department</button>} />
      <div style={card}>
        {rows.map(d => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #F3F0EA', fontSize: 13.5 }}>
            <div style={{ flex: 1 }}><b>{d.name}</b> <span style={{ color: '#9CA3AF', marginRight: 8 }}>{d.name_ar}</span></div>
            <button onClick={() => setModal({ id: d.id, name: d.name, name_ar: d.name_ar || '' })} style={{ ...btn('#F3F0EA', '#374151'), padding: '4px 12px', fontSize: 12 }}>Edit</button>
            <button onClick={() => remove(d.id)} style={{ ...btn('#FEF2F2', '#DC2626'), padding: '4px 12px', fontSize: 12 }}>Remove</button>
          </div>
        ))}
        {rows.length === 0 && <div style={{ padding: 16, color: '#9CA3AF', fontSize: 13 }}>No departments.</div>}
      </div>
      {modal && (
        <Modal title={modal.id ? 'Edit Department' : 'Add Department'} onClose={() => setModal(null)}>
          <label style={lbl}>Name (English) <span style={{ color: '#DC2626' }}>*</span></label>
          <input style={{ ...inp, marginBottom: 12, ...(modal._miss ? { borderColor: '#DC2626', background: '#FEF2F2' } : {}) }} value={modal.name} onChange={e => setModal({ ...modal, name: e.target.value, _miss: false })} autoFocus />
          <label style={lbl}>Name (Arabic)</label>
          <input style={{ ...inp, marginBottom: 16, direction: 'rtl' }} value={modal.name_ar} onChange={e => setModal({ ...modal, name_ar: e.target.value })} />
          <button onClick={() => { if (!modal.name) { setModal({ ...modal, _miss: true }); return; } save(); }} style={{ ...btn(), width: '100%' }}>{modal.id ? 'Save Changes' : 'Add Department'}</button>
        </Modal>
      )}
    </div>
  );
}

function AdminStamps() {
  const [stamps, setStamps] = useState([]);
  const [modal, setModal] = useState(null); // {name, file, preview}
  const [busy, setBusy] = useState(false);
  const load = () => edrmsApi.get('/stamps').then(setStamps).catch(() => setStamps([]));
  useEffect(() => { load(); }, []);
  const pick = (e) => { const file = e.target.files[0]; if (!file) return; setModal(m => ({ ...m, file, preview: URL.createObjectURL(file), name: m.name || file.name.replace(/\.png$/i, '') })); };
  const save = async () => {
    if (!modal.name || (!modal.id && !modal.file)) { setModal({ ...modal, _miss: true }); return; }
    setBusy(true);
    const fd = new FormData();
    if (modal.file) fd.append('file', modal.file);
    fd.append('name', modal.name);
    try {
      if (modal.id) await edrmsApi.postForm(`/stamps/${modal.id}`, fd).catch(async () => { await edrmsApi({ method: 'put', url: `/stamps/${modal.id}`, data: fd, headers: { 'Content-Type': 'multipart/form-data' } }); });
      else await edrmsApi.post('/stamps', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setModal(null); load();
    } catch (e) {}
    setBusy(false);
  };
  const remove = async (id) => { try { await edrmsApi.delete(`/stamps/${id}`); load(); } catch (e) {} };
  const token = localStorage.getItem('flowup_token');
  return (
    <div>
      <PageHead title="Stamps" desc="Upload as many PNG stamps as you need and give each a name. The signatory picks one when issuing a letter." action={<button onClick={() => setModal({ name: '', file: null, preview: '' })} style={btn()}>+ Add Stamp</button>} />
      {stamps.length === 0 && <div style={{ ...card, color: '#9CA3AF', fontSize: 13, textAlign: 'center', padding: 30 }}>No stamps yet. Add one or more named stamps (PNG with transparent background works best).</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 14 }}>
        {stamps.map(s => (
          <div key={s.id} style={{ ...card, textAlign: 'center', padding: 14 }}>
            <div style={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAF8', borderRadius: 8, marginBottom: 8 }}>
              <img src={`/api/edrms/stamps/${s.id}/image?token=${token}`} alt={s.name} style={{ maxWidth: 130, maxHeight: 100, objectFit: 'contain' }} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 750 }}>{s.name}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button onClick={() => setModal({ id: s.id, name: s.name, file: null, preview: '' })} style={{ ...btn('#F3F0EA', '#374151'), padding: '4px 12px', fontSize: 11.5, flex: 1 }}>Edit</button>
              <button onClick={() => remove(s.id)} style={{ ...btn('#FEF2F2', '#DC2626'), padding: '4px 12px', fontSize: 11.5 }}>Remove</button>
            </div>
          </div>
        ))}
      </div>
      {modal && (
        <Modal title={modal.id ? 'Edit Stamp' : 'Add Stamp'} onClose={() => setModal(null)}>
          <label style={lbl}>Stamp name <span style={{ color: '#DC2626' }}>*</span></label>
          <input style={{ ...inp, marginBottom: 14, ...(modal._miss && !modal.name ? { borderColor: '#DC2626', background: '#FEF2F2' } : {}) }} value={modal.name} onChange={e => setModal({ ...modal, name: e.target.value, _miss: false })} placeholder="e.g. Official Seal, Stamp 1…" autoFocus />
          <label style={lbl}>PNG image {modal.id ? '(leave empty to keep current)' : <span style={{ color: '#DC2626' }}>*</span>}</label>
          <div style={{ border: '2px dashed #E5E7EB', borderRadius: 10, padding: 20, textAlign: 'center', marginBottom: 16 }}>
            {modal.preview ? <img src={modal.preview} alt="" style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }} />
              : modal.id ? <img src={`/api/edrms/stamps/${modal.id}/image?token=${token}`} alt="" style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }} />
                : <div style={{ color: '#9CA3AF', fontSize: 13 }}>No image selected</div>}
            <label style={{ ...btn('#F3F0EA', '#374151'), cursor: 'pointer', display: 'inline-block', marginTop: 12 }}>Choose PNG<input type="file" accept=".png" onChange={pick} style={{ display: 'none' }} /></label>
          </div>
          <button onClick={save} disabled={busy} style={{ ...btn(), width: '100%' }}>{busy ? 'Saving…' : (modal.id ? 'Save Changes' : 'Add Stamp')}</button>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DOCUMENT REGISTER — Agreements, MoMs, MoUs, NDAs, Resolutions… (configurable)
// ═══════════════════════════════════════════════════════════════════════════
function RecordsSection({ me }) {
  const [types, setTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [editing, setEditing] = useState(null); // record object or {} for new
  const [unlockFor, setUnlockFor] = useState(null); // record awaiting password

  const loadTypes = useCallback(() => edrmsApi.get('/record-types').then(setTypes).catch(() => setTypes([])), []);
  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (typeFilter !== 'all') qs.set('type', typeFilter);
    if (yearFilter !== 'all') qs.set('year', yearFilter);
    return edrmsApi.get(`/records?${qs.toString()}`).then(setRows).catch(() => setRows([]));
  }, [typeFilter, yearFilter]);
  useEffect(() => { loadTypes(); }, [loadTypes]);
  useEffect(() => { load(); }, [load]);

  const years = Array.from(new Set(rows.map(r => r.year).filter(Boolean))).sort((a, b) => b - a);

  // open a record for editing; if locked, ask for the password first (in-app modal)
  const openRecord = (r) => {
    if (r.is_locked) { setUnlockFor(r); return; }
    setEditing(r);
  };

  return (
    <div>
      <PageHead title="Document Register" desc="Register Agreements, Minutes of Meeting, MoUs, NDAs, Resolutions and more — each with its document number, date, link and attachment."
        action={<button onClick={() => setEditing({})} style={btn()} disabled={!types.length}>+ New Record</button>} />

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ ...inp, width: 'auto' }}>
          <option value="all">All types</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={yearFilter} onChange={e => setYearFilter(e.target.value)} style={{ ...inp, width: 'auto' }}>
          <option value="all">All years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {!me.is_admin && <span style={{ fontSize: 12, color: '#9CA3AF', alignSelf: 'center' }}>You see the records you registered.</span>}
      </div>

      <div style={card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6B7280', borderBottom: '2px solid #F3F0EA' }}>
                <th style={{ padding: '8px 10px' }}>Type</th>
                <th style={{ padding: '8px 10px' }}>Doc No.</th>
                <th style={{ padding: '8px 10px' }}>Year</th>
                <th style={{ padding: '8px 10px' }}>Date</th>
                <th style={{ padding: '8px 10px' }}>Organization / Title</th>
                <th style={{ padding: '8px 10px' }}>Link</th>
                <th style={{ padding: '8px 10px' }}>File</th>
                <th style={{ padding: '8px 10px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} style={{ padding: 18, color: '#9CA3AF', textAlign: 'center' }}>No records yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #F6F4EF' }}>
                  <td style={{ padding: '8px 10px' }}>
                    {r.is_locked && <span title="Password protected" style={{ marginRight: 6 }}>🔒</span>}
                    <span style={{ background: '#EEF2FF', color: '#4F46E5', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 999 }}>{r.type_name}</span>
                  </td>
                  <td style={{ padding: '8px 10px', fontWeight: 700 }}>{r.doc_number || '—'}</td>
                  <td style={{ padding: '8px 10px' }}>{r.year || '—'}</td>
                  <td style={{ padding: '8px 10px' }}>{r.doc_date ? fmtDate(r.doc_date) : '—'}</td>
                  <td style={{ padding: '8px 10px' }}>{r.is_locked ? <span style={{ color: '#9CA3AF' }}>— locked —</span> : (r.has_org ? (r.org || '—') : (r.title || '—'))}</td>
                  <td style={{ padding: '8px 10px' }}>{r.is_locked ? '—' : (r.link ? <a href={r.link} target="_blank" rel="noreferrer" style={{ color: '#2563EB' }}>Open ↗</a> : '—')}</td>
                  <td style={{ padding: '8px 10px' }}>{r.is_locked ? '—' : (r.attachment_name ? <a href={`/api/edrms/records/${r.id}/attachment`} target="_blank" rel="noreferrer" style={{ color: '#2563EB' }}>⬇</a> : '—')}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <button onClick={() => openRecord(r)} style={{ ...btn('#F3F0EA', '#374151'), padding: '4px 10px', fontSize: 12 }}>{r.is_locked ? 'Unlock' : 'Open'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <RecordForm types={types} record={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {unlockFor && <UnlockModal kind="records" item={unlockFor} onClose={() => setUnlockFor(null)}
        onUnlocked={(pw) => { setUnlockFor(null); edrmsApi.get(`/records/${unlockFor.id}`, { headers: { 'x-edrms-lock': pw } }).then(full => setEditing({ ...full, _unlockPw: pw })).catch(() => {}); }} />}
    </div>
  );
}

// In-app modal to unlock a password-protected item.
function UnlockModal({ kind, item, onClose, onUnlocked }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setBusy(true); setErr('');
    try { await edrmsApi.post(`/${kind}/${item.id}/unlock`, { password: pw }); onUnlocked(pw); }
    catch (e) { setErr(e.error || 'Incorrect password.'); setBusy(false); }
  };
  return (
    <Modal title="Protected item" onClose={onClose} width={400}>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 34 }}>🔒</div>
        <div style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>This item is protected with a password. Enter it to open.</div>
      </div>
      <input type="password" autoFocus style={inp} value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()} placeholder="Password" />
      {err && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
      <button onClick={submit} disabled={busy || !pw} style={{ ...btn(), width: '100%', marginTop: 14 }}>{busy ? 'Checking…' : 'Unlock'}</button>
    </Modal>
  );
}

// Manage a password lock on an existing outgoing/incoming item (set / change / remove).
// `lockPw` is the password already used to open it (needed to change/remove when locked).
function LockManager({ kind, id, locked, lockPw, onChanged }) {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(lockPw || '');
  const [np, setNp] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const apply = async (remove) => {
    setBusy(true); setMsg('');
    const body = {};
    if (locked) body.current_password = cur;
    if (remove) body.remove = true; else body.new_password = np;
    try { await edrmsApi.post(`/${kind}/${id}/lock`, body); setOpen(false); setNp(''); onChanged && onChanged(); }
    catch (e) { setMsg(e.error || 'Failed.'); }
    setBusy(false);
  };
  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: '#FBFBF9', border: '1px solid #EFEDE8', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 15 }}>{locked ? '🔒' : '🔓'}</span>
        <div style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: '#374151' }}>{locked ? 'Protected with a password' : 'Add a password to lock this'}</div>
        <button onClick={() => setOpen(o => !o)} style={{ ...btn('#F3F0EA', '#374151'), padding: '4px 10px', fontSize: 12 }}>{open ? 'Close' : (locked ? 'Change / Remove' : 'Add password')}</button>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          {locked && <div style={{ marginBottom: 8 }}><label style={lbl}>Current password</label><input type="password" style={inp} value={cur} onChange={e => setCur(e.target.value)} /></div>}
          <label style={lbl}>{locked ? 'New password' : 'Password (min 4 chars)'}</label>
          <input type="password" style={inp} value={np} onChange={e => setNp(e.target.value)} placeholder="••••••" />
          <div style={{ fontSize: 11, color: '#9CA3AF', margin: '6px 0' }}>Once locked, it can only be opened with the password — no one can bypass it, including administrators.</div>
          {msg && <div style={{ color: '#B91C1C', fontSize: 12, marginBottom: 6 }}>{msg}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => apply(false)} disabled={busy || !np} style={{ ...btn(), flex: 1 }}>{locked ? 'Change' : 'Lock'}</button>
            {locked && <button onClick={() => apply(true)} disabled={busy} style={btn('#FEF2F2', '#DC2626')}>Remove lock</button>}
          </div>
        </div>
      )}
    </div>
  );
}

function RecordForm({ types, record, onClose, onSaved }) {
  const isNew = !record.id;
  const [f, setF] = useState(() => ({
    type_id: record.type_id || (types[0] && types[0].id) || '',
    doc_number: record.doc_number || '',
    year: record.year || new Date().getFullYear(),
    doc_date: record.doc_date ? String(record.doc_date).slice(0, 10) : '',
    org: record.org || '',
    title: record.title || '',
    link: record.link || '',
    notes: record.notes || '',
    attachment_name: record.attachment_name || '',
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [miss, setMiss] = useState({});
  const [pendingFile, setPendingFile] = useState(null); // file chosen before the record exists
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const t = types.find(x => x.id === f.type_id) || {};

  const save = async () => {
    const m = {};
    if (!f.type_id) m.type_id = true;
    if (!String(f.doc_number).trim()) m.doc_number = true;
    if (Object.keys(m).length) { setMiss(m); setErr('Please fill the required fields (★).'); return; }
    setMiss({}); setBusy(true); setErr('');
    try {
      let id = record.id;
      const headers = record.is_locked && f._unlockPw ? { 'x-edrms-lock': f._unlockPw } : {};
      if (isNew) { const r = await edrmsApi.post('/records', f); id = r.id; }
      else await edrmsApi.put(`/records/${record.id}`, f, { headers });
      // if a file was chosen while creating, upload it now that we have an id
      if (pendingFile && id) {
        const fd = new FormData(); fd.append('file', pendingFile);
        try { await edrmsApi.postForm(`/records/${id}/attachment`, fd); } catch (e) { /* record saved; attachment failed */ }
      }
      // apply / change / remove the password lock, if the user touched it
      if (id && (f.lock_password || (record.is_locked && f.lock_current))) {
        const body = {};
        if (record.is_locked) body.current_password = f.lock_current || '';
        if (f.lock_password) body.new_password = f.lock_password; else if (record.is_locked) body.remove = true;
        try { await edrmsApi.post(`/records/${id}/lock`, body); }
        catch (e) { setErr(e.error || 'Saved, but the password change failed.'); setBusy(false); return; }
      }
      onSaved(id);
    } catch (e) { setErr(e.error || 'Failed to save.'); }
    setBusy(false);
  };
  const remove = async () => {
    if (!record.id) return;
    setBusy(true);
    try { await edrmsApi.delete(`/records/${record.id}`); onSaved(); } catch (e) { setErr(e.error || 'Failed.'); setBusy(false); }
  };
  const star = <span style={{ color: '#DC2626' }}> ★</span>;
  const fs = (k) => ({ ...inp, ...(miss[k] ? { borderColor: '#DC2626', background: '#FEF2F2' } : {}) });

  return (
    <Modal title={isNew ? 'New Record' : 'Edit Record'} onClose={onClose} width={560}>
      <div style={{ marginBottom: 12 }}>
        <label style={lbl}>Document Type{star}</label>
        <select style={fs('type_id')} value={f.type_id} onChange={e => set('type_id', e.target.value)}>
          {types.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.5fr 0.7fr', gap: 10, marginBottom: 12 }}>
        <div><label style={lbl}>Document Number{star}</label><input style={fs('doc_number')} value={f.doc_number} onChange={e => set('doc_number', e.target.value)} /></div>
        <div><label style={lbl}>Year</label><input type="number" style={inp} value={f.year} onChange={e => set('year', e.target.value)} /></div>
        <div><label style={lbl}>Date</label><input type="date" style={inp} value={f.doc_date} onChange={e => set('doc_date', e.target.value)} /></div>
      </div>
      {t.has_org ? (
        <div style={{ marginBottom: 12 }}><label style={lbl}>Organization / Company</label><input style={inp} value={f.org} onChange={e => set('org', e.target.value)} /></div>
      ) : null}
      {t.has_title ? (
        <div style={{ marginBottom: 12 }}><label style={lbl}>{/Resolution/i.test(t.name) ? 'Title' : 'MoM Title'}</label><input style={inp} value={f.title} onChange={e => set('title', e.target.value)} /></div>
      ) : null}
      <div style={{ marginBottom: 12 }}><label style={lbl}>Link (OneDrive / SharePoint / URL)</label><input style={inp} value={f.link} onChange={e => set('link', e.target.value)} placeholder="https://…" /></div>
      <div style={{ marginBottom: 12 }}><label style={lbl}>Notes (optional)</label><textarea style={{ ...inp, minHeight: 52, resize: 'vertical' }} value={f.notes} onChange={e => set('notes', e.target.value)} /></div>
      {!isNew ? (
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Attachment file (optional)</label>
          <RecordAttachment id={record.id} current={f.attachment_name} onChange={n => set('attachment_name', n)} />
        </div>
      ) : (
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Attachment file (optional)</label>
          <input type="file" onChange={e => setPendingFile(e.target.files[0] || null)} style={{ fontSize: 12.5 }} />
          {pendingFile && <div style={{ fontSize: 12, color: '#16A34A', marginTop: 5 }}>✓ {pendingFile.name} — will be attached when you register.</div>}
        </div>
      )}
      <LockField f={f} set={set} isNew={isNew} locked={record.is_locked} />
      {err && <div style={{ color: '#B91C1C', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={busy} style={{ ...btn(), flex: 1 }}>{busy ? 'Saving…' : (isNew ? 'Register' : 'Save Changes')}</button>
        {!isNew && <button onClick={remove} disabled={busy} style={btn('#FEF2F2', '#DC2626')}>Delete</button>}
      </div>
    </Modal>
  );
}

// Password-lock control shown inside a create/edit form. `kind` + `id` identify the
// item; for a NEW item there's no id yet, so we stage the password and the parent
// applies it after creation (records flow). For existing items it calls the API.
function LockField({ f, set, isNew, locked }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 14, padding: '10px 12px', background: '#FBFBF9', border: '1px solid #EFEDE8', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16 }}>{(locked || f.lock_password) ? '🔒' : '🔓'}</span>
        <div style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: '#374151' }}>
          {locked ? 'Protected with a password' : 'Add a password to lock this item'}
        </div>
        <button type="button" onClick={() => setOpen(o => !o)} style={{ ...btn('#F3F0EA', '#374151'), padding: '4px 10px', fontSize: 12 }}>
          {open ? 'Close' : (locked ? 'Change / Remove' : 'Add password')}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          {locked && (
            <div style={{ marginBottom: 8 }}>
              <label style={lbl}>Current password</label>
              <input type="password" style={inp} value={f.lock_current || ''} onChange={e => set('lock_current', e.target.value)} placeholder="Required to change or remove" />
            </div>
          )}
          <label style={lbl}>{locked ? 'New password (leave empty to remove)' : 'Password (min 4 characters)'}</label>
          <input type="password" style={inp} value={f.lock_password || ''} onChange={e => set('lock_password', e.target.value)} placeholder="••••••" />
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>
            Once locked, this item can only be opened with the password — no one can bypass it, including administrators. Keep it safe; it can’t be recovered.
          </div>
        </div>
      )}
    </div>
  );
}

function RecordAttachment({ id, current, onChange }) {
  const [name, setName] = useState(current || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await edrmsApi.postForm(`/records/${id}/attachment`, fd);
      setName(r.attachment_name || file.name); onChange && onChange(r.attachment_name || file.name);
    } catch (e) { setErr(e.error || 'Upload failed.'); }
    setBusy(false);
  };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input type="file" onChange={e => upload(e.target.files[0])} disabled={busy} style={{ fontSize: 12.5 }} />
        {busy && <span style={{ fontSize: 12, color: '#9CA3AF' }}>Uploading…</span>}
      </div>
      {name && <div style={{ fontSize: 12, marginTop: 6 }}>Current: <a href={`/api/edrms/records/${id}/attachment`} target="_blank" rel="noreferrer" style={{ color: '#2563EB' }}>{name}</a></div>}
      {err && <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 4 }}>{err}</div>}
    </div>
  );
}

function AdminRecordTypes() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const load = () => edrmsApi.get('/record-types').then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!modal.name || !modal.name.trim()) { setModal({ ...modal, _miss: true }); return; }
    try {
      if (modal.id) await edrmsApi.put(`/record-types/${modal.id}`, modal);
      else await edrmsApi.post('/record-types', modal);
      setModal(null); load();
    } catch (e) { setModal({ ...modal, _err: e.error || 'Failed' }); }
  };
  const remove = async (r) => { try { await edrmsApi.delete(`/record-types/${r.id}`); load(); } catch (e) { alert(e.error || 'Failed'); } };
  return (
    <div>
      <PageHead title="Document Types" desc="Configure the record types used in the Document Register. Each type shows either an Organization/Company field, a Title field, or both."
        action={<button onClick={() => setModal({ name: '', code: '', has_org: true, has_title: false })} style={btn()}>+ Add Type</button>} />
      <div style={card}>
        {rows.map(r => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #F3F0EA', fontSize: 13.5 }}>
            <div style={{ flex: 1 }}>
              <b>{r.name}</b> {r.code ? <span style={{ color: '#9CA3AF', fontSize: 12 }}>· {r.code}</span> : null}
              {r.is_system ? <span style={{ marginLeft: 8, fontSize: 10.5, color: '#9CA3AF' }}>(default)</span> : null}
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>
                Fields: Document number · Year · Date · {r.has_org ? 'Organization/Company · ' : ''}{r.has_title ? 'Title · ' : ''}Link · Attachment
              </div>
            </div>
            <button onClick={() => setModal({ id: r.id, name: r.name, code: r.code || '', has_org: !!r.has_org, has_title: !!r.has_title, is_system: r.is_system })} style={{ ...btn('#F3F0EA', '#374151'), padding: '4px 12px', fontSize: 12 }}>Edit</button>
            {!r.is_system && <button onClick={() => remove(r)} style={{ ...btn('#FEF2F2', '#DC2626'), padding: '4px 12px', fontSize: 12 }}>Remove</button>}
          </div>
        ))}
        {rows.length === 0 && <div style={{ padding: 16, color: '#9CA3AF', fontSize: 13 }}>No types.</div>}
      </div>
      {modal && (
        <Modal title={modal.id ? 'Edit Type' : 'Add Type'} onClose={() => setModal(null)}>
          <label style={lbl}>Type name <span style={{ color: '#DC2626' }}>★</span></label>
          <input style={{ ...inp, marginBottom: 12, ...(modal._miss ? { borderColor: '#DC2626', background: '#FEF2F2' } : {}) }} value={modal.name} onChange={e => setModal({ ...modal, name: e.target.value, _miss: false })} placeholder="e.g. Contracts" autoFocus />
          <label style={lbl}>Code / prefix (optional)</label>
          <input style={{ ...inp, marginBottom: 14 }} value={modal.code} onChange={e => setModal({ ...modal, code: e.target.value })} placeholder="e.g. CTR" />
          <label style={lbl}>Type-specific field</label>
          <div style={{ display: 'flex', gap: 16, marginBottom: 14, fontSize: 13 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><input type="checkbox" checked={modal.has_org} onChange={e => setModal({ ...modal, has_org: e.target.checked })} /> Organization / Company</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><input type="checkbox" checked={modal.has_title} onChange={e => setModal({ ...modal, has_title: e.target.checked })} /> Title</label>
          </div>
          <div style={{ fontSize: 11.5, color: '#9CA3AF', marginBottom: 14 }}>Document number, Year, Date, Link and Attachment are always included.</div>
          {modal._err && <div style={{ color: '#B91C1C', fontSize: 12.5, marginBottom: 10 }}>{modal._err}</div>}
          <button onClick={save} style={{ ...btn(), width: '100%' }}>{modal.id ? 'Save Changes' : 'Add Type'}</button>
        </Modal>
      )}
    </div>
  );
}
